import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { get } from '../../utils/api'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material'
import styles from '../../styles/gri/report.module.css'
import tableStyles from '../../styles/gri/styledReportTable.module.css'

const GRI400 = () => {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Define inputData structures for questions and titles
  const inputData401_1a = {
    fieldName: "newEmployeeHire",
    title: "Please provide the total number of new employees hired during the reporting period, categorized by age group, gender, and region.",
    letter: "a.",
    fields: [
      {
        fieldName: "ageGroup",
        title: "Age Group",
        type: "text",
      },
      {
        fieldName: "numberOfEmployees",
        title: "Number of new employees hired",
        type: "number",
      },
      {
        fieldName: "rateofHire",
        title: "Rate of new hire",
        type: "percentage",
      },
    ],
  }

  const inputData401_1b = {
    fieldName: "employeeTurnover",
    title: "What was the total number of employee turnovers within the reporting period, categorized by age group, gender, and region?",
    letter: "b.",
    fields: [
      {
        fieldName: "ageGroup",
        title: "Age Group",
        type: "text",
      },
      {
        fieldName: "turnover",
        title: "Employee turnover",
        type: "number",
      },
      {
        fieldName: "rateofTurnover",
        title: "Rate of employee turnover",
        type: "percentage",
      },
    ],
  }

  const inputData401_2a = {
    fieldName: "benifitProvided",
    title: "Can you please specify the standard benefits provided exclusively to full-time employees of the organization, excluding temporary or part-time employees? This information should be broken down by significant locations of operation and include, at a minimum?",
    letter: "a.",
    fields: [
      {
        fieldName: "benefit",
        title: "Benefits",
        type: "text",
      },
      {
        fieldName: "noOfEmployeesBenifited",
        title: "No. of Employees Benefited",
        type: "number",
      },
      {
        fieldName: "amount",
        title: "Amount",
        type: "number",
      },
    ],
  }

  const questionData401_2b = {
    fieldName: "significantLocationsOfOperation",
    title: "Define what is meant by 'significant locations of operation':",
    letter: "b.",
  }

  // Question data for 401-3 Parental Leaves
  const questionData401_3 = [
    {
      fieldName: "eligibleForParentalLeave",
      title: "How many employees were eligible for parental leave during the reporting period, categorized by gender?",
      letter: "a.",
    },
    {
      fieldName: "availedParentalLeave",
      title: "How many employees availed parental leave during the reporting period, categorized by gender?",
      letter: "b.",
    },
    {
      fieldName: "returnedAfterParentalLeave",
      title: "How many employees returned to work after the conclusion of their parental leave during the reporting period, categorized by gender?",
      letter: "c.",
    },
    {
      fieldName: "returnedWorkAfterParentalLeave",
      title: "Of the employees who returned to work after parental leave during the reporting period, how many remained employed 12 months after their return, categorized by gender?",
      letter: "d.",
    },
    {
      fieldName: "returnToWorkAndRetentionRates",
      title: "What were the return-to-work and retention rates of employees who took parental leave during the reporting period, categorized by gender?",
      letter: "e.",
      subFields: [
        {
          fieldName: "returnToWork",
          subHeading: "Return to work rate (%)",
        },
        {
          fieldName: "retentionRates",
          subHeading: "Retention rates of employees (%)",
        },
      ],
    },
  ]

  // Question data for 402-1 Labour Management Relations
  const questionData402_1 = [
    {
      fieldName: "noticePeriodDays",
      title: "What is the standard minimum duration of notice typically given to employees and their representatives before significant operational changes that may substantially impact them are implemented?",
      letter: "a.",
    },
    {
      fieldName: "collectiveBargaining",
      title: "Does the organization have collective bargaining agreements in place? If so, are the notice periods and details regarding consultation and negotiation specified within these collective agreements?",
      letter: "b.",
    },
  ]

  // Question data for 403-1 Occupational Health and Safety Management System
  const questionData403_1 = [
    {
      fieldName: "occupationalHealthDetails",
      title: "Has an occupational health and safety management system been established within the organization?",
      letter: "a.",
      subQuestions: [
        {
          fieldName: "legalRequirements",
          title: "Was the implementation of system driven by legal requirements? If so, please provide a list of the relevant legal requirements.",
          number: "1.",
        },
        {
          fieldName: "riskIdentification",
          title: "Was the implementation of system based on recognized risk management and/or management system standards/guidelines? If yes, please provide a list of the standards/guidelines utilized.",
          number: "2.",
        },
      ],
    },
    {
      fieldName: "coverageProvided",
      title: "Could you provide a comprehensive description of the extent of coverage provided by the occupational health and safety management system concerning workers, activities, and workplaces? Additionally, please explain whether any workers, activities, or workplaces are not covered by the system and, if so, provide the rationale behind their exclusion.",
      letter: "b.",
    },
  ]

  // Question data for 403-2 Hazard Identification, Risk Assessment, and Incident Investigation
  const questionData403_2 = [
    {
      fieldName: "detailedOverview",
      title: "Could you provide a detailed overview of the procedures employed by the organization to identify both routine and non-routine work-related hazards, assess associated risks, and utilize the hierarchy of controls to eliminate hazards and mitigate risks?",
      letter: "a.",
      subQuestions: [
        {
          fieldName: "ensuredEffectiveness",
          title: "How does the organization ensure the effectiveness and quality of these processes, particularly concerning the competency of individuals involved?",
          number: "1.",
        },
        {
          fieldName: "outcome",
          title: "How are the outcomes of these processes utilized to assess and continuously enhance the occupational health and safety management system?",
          number: "2.",
        },
      ],
    },
    {
      fieldName: "workerHazardReportingMechanisms",
      title: "Please describe the mechanisms in place for workers to report work-related hazards and hazardous situations. Additionally, explain the safeguards implemented to protect workers against any potential reprisals.",
      letter: "b.",
    },
    {
      fieldName: "workerSafetyWithdrawalPolicies",
      title: "Could you outline the organization's policies and procedures enabling workers to remove themselves from work environments they perceive as potentially hazardous to their health or safety? Furthermore, elucidate the measures in place to safeguard workers from potential reprisals.",
      letter: "c.",
    },
    {
      fieldName: "incidentInvestigationProcedures",
      title: "Describe the procedures utilized to investigate work-related incidents, encompassing the identification of hazards and risk assessment related to the incidents, determination of corrective actions through the hierarchy of controls, and identification of necessary improvements to the occupational health and safety management system.",
      letter: "d.",
    },
  ]

  // Question data for 403-3 Occupational Health Services
  const questionData403_3 = [
    {
      fieldName: "occupationalHealthServicesFunctions",
      title: "Describe the functions performed by occupational health services that contribute to the identification and elimination of hazards, as well as the minimization of risks? Additionally, please explain the measures taken by the organization to ensure the quality of these services and to facilitate workers' access to them.",
      letter: "a.",
      subQuestions: [
        {
          fieldName: "workerHealthInfoConfidentiality",
          title: "How does the organization maintain the confidentiality of workers' personal health related information",
          number: "1.",
        },
        {
          fieldName: "workerHealthInfoUsageProtection",
          title: "How does the organization ensures that workers' personal health-related information and their participation in any occupational health services are not used for any favourable or unfavourable treatment of workers.",
          number: "2.",
        },
      ],
    },
  ]

  // Question data for 403-4 Worker Participation, Consultation, and Communication
  const questionData403_4 = [
    {
      fieldName: "workerPaticipation",
      title: "Describe the procedures employed for worker participation and consultation throughout the development, implementation, and evaluation phases of the occupational health and safety management system? Additionally, please explain how the organization facilitates access to and communicates pertinent information regarding occupational health and safety to its workers.",
      letter: "a.",
    },
    {
      fieldName: "healthSafetyCommitteeDetails",
      title: "If formal joint management-worker health and safety committees are established, could you describe their roles, frequency of meetings, authority in decision-making, and rationale for any workers not being represented by these committees, if applicable?",
      letter: "b.",
    },
  ]

  // Question data for 403-5 Worker Training on Occupational Health and Safety
  const questionData403_5 = [
    {
      fieldName: "workerOHSTraining",
      title: "Describe the occupational health and safety training offered to workers, encompassing both general training and specialized training on identified work-related hazards, hazardous activities, or situations?",
      letter: "a.",
    },
  ]

  // Question data for 403-6 Promotion of Worker Health
  const questionData403_6 = [
    {
      fieldName: "workerHealthcareAccess",
      title: "How does the organization enable workers to access non-occupational medical and healthcare services? Please provide details regarding the extent of access provided.",
      letter: "a.",
    },
    {
      fieldName: "voluntaryHealthPromotionPrograms",
      title: "Describe any voluntary health promotion services and programs offered to workers aimed at mitigating major non-work-related health risks? Additionally, please specify the particular health risks targeted and explain how the organization facilitates workers' access to these services and programs.",
      letter: "b.",
      subQuestions: [
        {
          fieldName: "healthInformationConfidentialityMeasures",
          title: "How does the organization maintains the confidentiality of workers' personal health related information?",
          number: "1.",
        },
        {
          fieldName: "healthInfoPrivacyNonDiscrimination",
          title: "How does the organization ensures that workers' personal health-related information and their participation in any services or programs is not used for any favourable or unfavourable treatment of workers",
          number: "2.",
        },
      ],
    },
  ]

  // Question data for 403-7 Prevention and Mitigation of OHS Impacts
  const questionData403_7 = [
    {
      fieldName: "occupationalHealthPreventionMitigation",
      title: "Describe organization's approach for preventing or alleviating significant adverse impacts on occupational health and safety that may arise directly from its operations, products, or services through its business relationships? Additionally, please outline the associated hazards and risks",
      letter: "a.",
    },
  ]

  // Question data for 403-8 Workers Covered by OHS Management System
  const questionData403_8 = [
    {
      fieldName: "OHSManagementSystem",
      title: "Mention if the organization has established an occupational health and safety management system in alignment with legal requirements and/or recognized standards/guidelines:",
      letter: "a.",
      subQuestions: [
        {
          fieldName: "OHSSystemCoverageDetails",
          title: "What is the total number and percentage of individuals, including employees and nonemployee workers whose work and/or workplace is under the organization's control, covered by such a system?",
          number: "1.",
        },
        {
          fieldName: "OHSSystemInternalAuditCoverage",
          title: "Among the total number mentioned in (i), what is the number and percentage covered by an internally audited occupational health and safety management system?",
          number: "2.",
        },
        {
          fieldName: "WorkerExclusionReasons",
          title: "Among the total number mentioned in (i), what is the number and percentage covered by an externally audited or certified occupational health and safety management system?",
          number: "3.",
        },
      ],
    },
    {
      fieldName: "excludedWorkers",
      title: "Explain whether any workers have been excluded from the disclosed data, and if so, provide the reasons for their exclusion? Additionally, please specify the types of workers excluded.",
      letter: "b.",
    },
    {
      fieldName: "contextualInformation",
      title: "Provide any contextual information necessary to comprehend how the data were compiled. This may include details on the standards, methodologies, and assumptions utilized in the data collection process.",
      letter: "c.",
    },
  ]

  useEffect(() => {
    fetchData()
  }, [id])

  const fetchData = async () => {
    try {
      setLoading(true)
      const res = await get(`/gri/get?type=GRI400&Id=${id}`)
      setData(res?.data?.data?.GRI400)
    } catch (err) {
      console.log('Error fetching GRI400 data:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
  return (
      <div className={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    )
  }

  // Extract data from state
  const employeeHire = data?.employeeHire || {}
  const benefitsData = data?.benifitsData || {}
  const parentalLeavesData = data?.parentalLeavesData || {}
  const labourManagement = data?.labourManagement || {}
  const healthAndSafety = data?.healthAndSafety || {}

  // Process employee hire data for display - group by region
  const processEmployeeHireData = () => {
    if (!employeeHire?.newEmployeeHire || !Array.isArray(employeeHire.newEmployeeHire)) {
      return []
    }

    const processed = []
    employeeHire.newEmployeeHire.forEach((regionData) => {
      const region = regionData.region || 'N/A'
      const regionGroup = { region, genders: [] }
      
      // Process Male data
      if (regionData.male && Array.isArray(regionData.male) && regionData.male.length > 0) {
        const maleRows = regionData.male.map((item, index) => ({
          sno: index + 1,
          ageGroup: item.ageGroup || '-',
          numberOfEmployees: item.numberOfEmployees || 0,
          rateOfHire: item.rateofHire || 0,
          isTotal: false,
        }))
        
        // Add total row for male using maleTotal from API
        const maleTotal = regionData.maleTotal?.numberOfEmployees || regionData.male.reduce((sum, item) => sum + (parseInt(item.numberOfEmployees) || 0), 0)
        
        maleRows.push({
          sno: 'Total',
          ageGroup: '',
          numberOfEmployees: maleTotal,
          rateOfHire: '',
          isTotal: true,
        })

        regionGroup.genders.push({
          gender: 'Male',
          rows: maleRows,
        })
      }

      // Process Female data
      if (regionData.female && Array.isArray(regionData.female) && regionData.female.length > 0) {
        const femaleRows = regionData.female.map((item, index) => ({
          sno: index + 1,
          ageGroup: item.ageGroup || '-',
          numberOfEmployees: item.numberOfEmployees || 0,
          rateOfHire: item.rateofHire || 0,
          isTotal: false,
        }))
        
        // Add total row for female using femaleTotal from API
        const femaleTotal = regionData.femaleTotal?.numberOfEmployees || regionData.female.reduce((sum, item) => sum + (parseInt(item.numberOfEmployees) || 0), 0)
        
        femaleRows.push({
          sno: 'Total',
          ageGroup: '',
          numberOfEmployees: femaleTotal,
          rateOfHire: '',
          isTotal: true,
        })

        regionGroup.genders.push({
          gender: 'Female',
          rows: femaleRows,
        })
      }

      if (regionGroup.genders.length > 0) {
        processed.push(regionGroup)
      }
    })

    return processed
  }

  // Process employee turnover data for display - group by region
  const processEmployeeTurnoverData = () => {
    if (!employeeHire?.employeeTurnover || !Array.isArray(employeeHire.employeeTurnover)) {
      return []
    }

    const processedTurnover = []
    employeeHire.employeeTurnover.forEach((regionData) => {
      const region = regionData.region || 'N/A'
      const regionGroup = { region, genders: [] }
      
      // Process Male data
      if (regionData.male && Array.isArray(regionData.male) && regionData.male.length > 0) {
        const maleRows = regionData.male.map((item, index) => ({
          sno: index + 1,
          ageGroup: item.ageGroup || '-',
          turnover: item.turnover || 0,
          rateOfTurnover: item.rateofTurnover || 0,
          isTotal: false,
        }))
        
        // Calculate total for male
        const maleTotal = regionData.male.reduce((sum, item) => sum + (parseInt(item.turnover) || 0), 0)
        maleRows.push({
          sno: 'Total',
          ageGroup: '',
          turnover: maleTotal,
          rateOfTurnover: '',
          isTotal: true,
        })

        regionGroup.genders.push({
          gender: 'Male',
          rows: maleRows,
        })
      }

      // Process Female data
      if (regionData.female && Array.isArray(regionData.female) && regionData.female.length > 0) {
        const femaleRows = regionData.female.map((item, index) => ({
          sno: index + 1,
          ageGroup: item.ageGroup || '-',
          turnover: item.turnover || 0,
          rateOfTurnover: item.rateofTurnover || 0,
          isTotal: false,
        }))
        
        // Calculate total for female
        const femaleTotal = regionData.female.reduce((sum, item) => sum + (parseInt(item.turnover) || 0), 0)
        femaleRows.push({
          sno: 'Total',
          ageGroup: '',
          turnover: femaleTotal,
          rateOfTurnover: '',
          isTotal: true,
        })

        regionGroup.genders.push({
          gender: 'Female',
          rows: femaleRows,
        })
      }

      if (regionGroup.genders.length > 0) {
        processedTurnover.push(regionGroup)
      }
    })

    return processedTurnover
  }

  // Processed data
  const processedData = processEmployeeHireData()
  const turnoverData = processEmployeeTurnoverData()

  // Benefits data
  const benefitProvided = benefitsData?.benifitProvided || []
  const significantLocationsOfOperation = benefitsData?.significantLocationsOfOperation || ''

  // Parental leaves data
  const eligibleForParentalLeave = parentalLeavesData?.eligibleForParentalLeave || {}
  const availedParentalLeave = parentalLeavesData?.availedParentalLeave || {}
  const returnedAfterParentalLeave = parentalLeavesData?.returnedAfterParentalLeave || {}
  const returnedWorkAfterParentalLeave = parentalLeavesData?.returnedWorkAfterParentalLeave || {}
  const returnToWorkAndRetentionRates = parentalLeavesData?.returnToWorkAndRetentionRates || {}

  // Labour management data
  const noticePeriodDays = labourManagement?.noticePeriodDays || ''
  const hasCollectiveBargaining = labourManagement?.booleanfield || false
  const collectiveBargainingNoticeDetails = labourManagement?.collectiveBargainingNoticeDetails || {}
  const collectiveBargainingDays = collectiveBargainingNoticeDetails?.days || ''

  // Health and safety data
  const occupationalHealthDetails = healthAndSafety?.occupationalHealthDetails || {}
  const legalRequirements = healthAndSafety?.legalRequirements || {}
  const riskIdentification = healthAndSafety?.riskIdentification || {}
  const coverageProvided = healthAndSafety?.coverageProvided || {}

  // Hazard identification data
  const hazardIdentification = data?.hazardIdentification || {}
  const detailedOverview = hazardIdentification?.detailedOverview || {}
  const procedureEmployed = detailedOverview?.procedureEmployed || ''
  const ensuredEffectiveness = detailedOverview?.ensuredEffectiveness || ''
  const outcome = detailedOverview?.outcome || ''
  const workerHazardReportingMechanisms = hazardIdentification?.workerHazardReportingMechanisms || ''
  const workerSafetyWithdrawalPolicies = hazardIdentification?.workerSafetyWithdrawalPolicies || ''
  const incidentInvestigationProcedures = hazardIdentification?.incidentInvestigationProcedures || ''

  // Occupational health services data (403-3)
  const occupationalHealthServices = data?.occupationalHealthServices || {}
  const occupationalHealthServicesFunctions = occupationalHealthServices?.occupationalHealthServicesFunctions || ''
  const workerHealthInfoConfidentiality = occupationalHealthServices?.workerHealthInfoConfidentiality || ''
  const workerHealthInfoUsageProtection = occupationalHealthServices?.workerHealthInfoUsageProtection || ''

  // Worker OHS participation data (403-4)
  const workerOHSParticipation = data?.workerOHSParticipation || {}
  const workerPaticipation = workerOHSParticipation?.workerPaticipation || ''
  const healthSafetyCommitteeDetails = workerOHSParticipation?.healthSafetyCommitteeDetails || ''

  // Worker OHS training data (403-5)
  const workerOHSTraining = data?.workerOHSTraining || ''

  // Promotion of worker health data (403-6)
  const promotionWorkerHealth = data?.promotionWorkerHealth || {}
  const workerHealthcareAccess = promotionWorkerHealth?.workerHealthcareAccess || ''
  const voluntaryHealthPromotion = promotionWorkerHealth?.voluntaryHealthPromotion || {}
  const voluntaryHealthPromotionPrograms = voluntaryHealthPromotion?.voluntaryHealthPromotionPrograms || ''
  const healthInformationConfidentialityMeasures = voluntaryHealthPromotion?.healthInformationConfidentialityMeasures || ''
  const healthInfoPrivacyNonDiscrimination = voluntaryHealthPromotion?.healthInfoPrivacyNonDiscrimination || ''

  // Occupational health prevention mitigation data (403-7)
  const occupationalHealthPreventionMitigation = data?.occupationalHealthPreventionMitigation || ''

  // Workers covered by OHS management system data (403-8)
  const workersCoveredOccupationalHealth = data?.workersCoveredOccupationalHealth || {}
  const OHSManagementSystem = workersCoveredOccupationalHealth?.OHSManagementSystem || {}
  const OHSManagementSystemAlignmentDetails = OHSManagementSystem?.OHSManagementSystemAlignmentDetails || ''
  const OHSSystemCoverageDetails = OHSManagementSystem?.OHSSystemCoverageDetails || ''
  const OHSSystemInternalAuditCoverage = OHSManagementSystem?.OHSSystemInternalAuditCoverage || ''
  const WorkerExclusionReasons = OHSManagementSystem?.WorkerExclusionReasons || ''
  const excludedWorkers = workersCoveredOccupationalHealth?.excludedWorkers || ''
  const contextualInformation = workersCoveredOccupationalHealth?.contextualInformation || ''

  // Work-related injuries data (403-9)
  const workedRelatedInjury = data?.workedRelatedInjury || {}
  const forAllEmployees = workedRelatedInjury?.forAllEmployees || {}
  const workplaceController = workedRelatedInjury?.workplaceController || {}
  const workHazards = workedRelatedInjury?.workHazards || {}
  
  // For all employees data
  const employeeFatalities = forAllEmployees?.fatalities || ''
  const employeeRateOfFatalities = forAllEmployees?.rateOfFatalities || ''
  const employeeRemarkFatalities = forAllEmployees?.remarkFatalities || ''
  const employeeHighConsequenceInjuries = forAllEmployees?.highConsequenceInjuries || ''
  const employeeRateOfHighConsequenceInjuries = forAllEmployees?.rateOfHighConsequenceInjuries || ''
  const employeeRemarkHighConsequence = forAllEmployees?.remarkHighConsequence || ''
  const employeeRecordableInjuries = forAllEmployees?.recordableInjuries || ''
  const employeeRateOfRecordableInjuries = forAllEmployees?.rateOfRecordableInjuries || ''
  const employeeRemarkRecordable = forAllEmployees?.remarkRecordable || ''
  const employeeNoOfHourWorked = forAllEmployees?.noOfHourWorked || ''
  const employeeRemarkHoured = forAllEmployees?.remarkHoured || ''
  const employeeWorkRelatedInjuryTypes = forAllEmployees?.workRelated || []
  
  // For workplace controller data
  const workplaceFatalities = workplaceController?.workplacefatalities || ''
  const workplaceRateOfFatalities = workplaceController?.workplacerateOfFatalities || ''
  const workplaceRemarkFatalities = workplaceController?.workplaceremarkFatalities || ''
  const workplaceHighConsequenceInjuries = workplaceController?.workplacehighConsequenceInjuries || ''
  const workplaceRateOfHighConsequenceInjuries = workplaceController?.workplacerateOfHighConsequenceInjuries || ''
  const workplaceRemarkHighConsequence = workplaceController?.workplaceremarkHighConsequence || ''
  const workplaceRecordableInjuries = workplaceController?.workplacerecordableInjuries || ''
  const workplaceRateOfRecordableInjuries = workplaceController?.workplacerateOfRecordableInjuries || ''
  const workplaceRemarkRecordable = workplaceController?.workplaceremarkRecordable || ''
  const workplaceNoOfHourWorked = workplaceController?.workplacenoOfHourWorked || ''
  const workplaceRemarkHoured = workplaceController?.workplaceremarkHoured || ''
  const workplaceWorkRelatedInjuryTypes = workplaceController?.workRelated || []
  
  // Work hazards data
  const hazardsDetermined = workHazards?.determined || ''
  const hazardsHighConsequence = workHazards?.highConsequence || ''
  const hazardsActions = workHazards?.hierachyOfControls || []
  const hazardsAndMinizeRisk = workedRelatedInjury?.hazardsAndMinizeRisk || ''
  const wheatherTheRatesCalulated = workedRelatedInjury?.wheatherTheRatesCalulated || false
  const workerExcluded = workedRelatedInjury?.workerExcluded || false
  const workRelatedContextualInformation = workedRelatedInjury?.contextualInformation || ''

  // Work-related ill health data (403-10)
  const workedRelatedIllHealth = data?.workedRelatedIllHealth || {}
  const illHealthForAllEmployees = workedRelatedIllHealth?.forAllEmployees || {}
  const illHealthWorkplaceController = workedRelatedIllHealth?.workplaceController || {}
  const illHealthWorkHazards = workedRelatedIllHealth?.workHazards || {}
  
  // For all employees ill health data
  const illHealthEmployeeFatalities = illHealthForAllEmployees?.fatalitiesRateWorkRelated || ''
  const illHealthEmployeeRemarkFatalities = illHealthForAllEmployees?.remarkFatalities || ''
  const illHealthEmployeeRecordable = illHealthForAllEmployees?.recordableWorkHealth || ''
  const illHealthEmployeeRemarkRecordable = illHealthForAllEmployees?.remarkrecordableWorkHealth || ''
  const illHealthEmployeeTypes = illHealthForAllEmployees?.controlledByOrg || []
  
  // For workplace controller ill health data
  const illHealthWorkplaceFatalities = illHealthWorkplaceController?.nunberRateFatalites || ''
  const illHealthWorkplaceRemarkFatalities = illHealthWorkplaceController?.fatalitiesAsRemark || ''
  const illHealthWorkplaceRecordable = illHealthWorkplaceController?.workplacerecordableWorkHealth || ''
  const illHealthWorkplaceRemarkRecordable = illHealthWorkplaceController?.workplaceWorkHealth || ''
  const illHealthWorkplaceTypes = illHealthWorkplaceController?.controlledByOrg || []
  
  // Ill health work hazards data
  const illHealthHazardsDetermined = illHealthWorkHazards?.determined || ''
  const illHealthHazardsHighConsequence = illHealthWorkHazards?.highConsequence || ''
  const illHealthHazardsActions = illHealthWorkHazards?.hierachyOfControls || []
  const illHealthHazardsAndMinizeRisk = workedRelatedIllHealth?.hazardsAndMinizeRisk || ''
  const illHealthContextualInformation = workedRelatedIllHealth?.contextualInformation || ''

  // Training and Education data (404-1, 404-2, 404-3)
  const averageTrainingHours = data?.AverageTrainingHoursPerEmployee || {}
  const trainingCategory = averageTrainingHours?.category || []
  const trainingGroups = averageTrainingHours?.groups || []
  
  const skillDevelopmentPrograms = data?.skillDevelopmentPrograms || {}
  const skillEnhancementInitiatives = skillDevelopmentPrograms?.skillEnhancementInitiatives || ''
  const careerTransitionPrograms = skillDevelopmentPrograms?.careerTransitionPrograms || ''
  
  const performanceAndCareerDevelopment = data?.perfomanceAndCareerDevlopment || []

  // Diversity and Equal Opportunity data (405-1, 405-2)
  const diversityOfGovernanceBody = data?.dicersityOfGoveranceBody || {}
  const governanceDiversityPercentage = diversityOfGovernanceBody?.governanceDiversityPercentage || {}
  const employeeDiversityPercentage = diversityOfGovernanceBody?.employeeDiversityPercentage || {}
  
  // Governance diversity data
  const governanceGender = governanceDiversityPercentage?.gender || []
  const governanceAgeGroups = governanceDiversityPercentage?.ageGroups || []
  const governanceOther = governanceDiversityPercentage?.other || []
  
  // Employee diversity data
  const employeeGender = employeeDiversityPercentage?.gender || []
  const employeeAgeGroups = employeeDiversityPercentage?.ageGroups || []
  const employeeOther = employeeDiversityPercentage?.other || []
  
  // Ratio of basic salary data (405-2)
  const ratioBasicSalary = data?.ratioBasicSalary || {}
  const manWomenSalaryRatio = ratioBasicSalary?.manWomenSalaryRatio || []
  const significantLocationsOfOperationSalary = ratioBasicSalary?.significantLocationsOfOperation || ''

  // Non-discrimination data (406)
  const nonDiscrimination = data?.nonDiscrimination || {}
  const incidentsOfDiscrimination = nonDiscrimination?.incidentsOfDiscrimination || {}
  const discriminationNumber = incidentsOfDiscrimination?.number || ''
  const discriminationRemark = incidentsOfDiscrimination?.remark || ''
  const statusActions = nonDiscrimination?.statusActions || {}
  const discriminationIncidentStatusActions = statusActions?.discriminationIncidentStatusActions || ''
  const incidentReviewed = statusActions?.incidentReviewed || ''
  const remediationPlansImplemented = statusActions?.remediationPlansImplemented || ''
  const remediationPlansDetails = statusActions?.remediationPlansDetails || ''
  const noLongerSubject = statusActions?.noLongerSubject || ''

  // Freedom of association data (407)
  const freedomOfAssociation = data?.freedomOfAssociation || {}
  const atRiskOperationsAndSuppliers = freedomOfAssociation?.atRiskOperationsAndSuppliers || {}
  const atRiskSuppliers = atRiskOperationsAndSuppliers?.suppliers || ''
  const operationAndSupplierType = atRiskOperationsAndSuppliers?.operationAndSupplierType || ''
  const atRiskCountriesOrAreas = atRiskOperationsAndSuppliers?.atRiskCountriesOrAreas || ''
  const collectiveBargaining = freedomOfAssociation?.collectiveBargaining || ''

  // Child labour data (408)
  const childLabourpoints = data?.childLabourpoints || {}
  const significantRiskIncidents = childLabourpoints?.SignificantRiskIncidents || {}
  const childLabourIncidents = significantRiskIncidents?.incidents || ''
  const childLabour = significantRiskIncidents?.childLabour || ''
  const youngWorkers = significantRiskIncidents?.youngWorkers || ''
  const significantRiskChildLabors = childLabourpoints?.significantRiskChildLabors || {}
  const childLabors = significantRiskChildLabors?.childLabors || ''
  const operationTypeAndSupplier = significantRiskChildLabors?.operationTypeAndSupplier || ''
  const countriesOrRegionsAtRisk = significantRiskChildLabors?.countriesOrRegionsAtRisk || ''
  const measuresToAbolishChildLabor = childLabourpoints?.measuresToAbolishChildLabor || ''

  // Forced or compulsory labour data (409)
  const forcedOrCompulsory = data?.forcedOrCompulsory || {}
  const riskOperationsForcedLabor = forcedOrCompulsory?.riskOperationsForcedLabor || {}
  const riskOperations = riskOperationsForcedLabor?.riskOperations || ''
  const typeOfOperation = riskOperationsForcedLabor?.typeOfOperation || ''
  const countriesOrGeographic = riskOperationsForcedLabor?.countriesOrGeographic || ''
  const forcedLaborActions = forcedOrCompulsory?.forcedLaborActions || ''

  // Security practices data (410)
  const securityPractices = data?.securityPractices || {}
  const securityPercentage = securityPractices?.percentage || ''
  const thirdPartyOrg = securityPractices?.thirdPartyOrg || {}
  const thirdPartyOrgStatus = thirdPartyOrg?.status || false
  const thirdPartyOrgRemarks = thirdPartyOrg?.remarks || ''

  // Rights of indigenous people data (411)
  const rightsOfIndigenous = data?.rightsOfIndigenous || {}
  const indigenousRightsViolationIncidents = rightsOfIndigenous?.indigenousRightsViolationIncidents || ''
  const reviewOrganization = rightsOfIndigenous?.reviewOrganization || false
  const remediationPlans = rightsOfIndigenous?.remediationPlans || false
  const indigenousRemediationPlansImplemented = rightsOfIndigenous?.remediationPlansImplemented || {}
  const indigenousRemediationPlansImplementedStatus = indigenousRemediationPlansImplemented?.status || false
  const indigenousRemediationPlansImplementedRemarks = indigenousRemediationPlansImplemented?.remarks || ''
  const indigenousNoLongerSubject = rightsOfIndigenous?.noLongerSubject || {}
  const indigenousNoLongerSubjectStatus = indigenousNoLongerSubject?.status || false
  const indigenousNoLongerSubjectRemarks = indigenousNoLongerSubject?.remarks || ''

  // Local communities data (413-1)
  const operationWithLocalCommunities = data?.operationWithLocalCommunities || {}
  const identificationAndImplementationProcedure = operationWithLocalCommunities?.identificationAndImplementationProcedure || ''
  const socialImpactAssessments = operationWithLocalCommunities?.socialImpactAssessments || {}
  const socialImpactAssessmentsIncludingGender = operationWithLocalCommunities?.socialImpactAssessmentsIncludingGender || {}
  const publicDisclosure = operationWithLocalCommunities?.publicDisclosure || {}
  const devlopmentLocalCommunity = operationWithLocalCommunities?.devlopmentLocalCommunity || {}
  const implmentationStackholder = operationWithLocalCommunities?.implmentationStackholder || {}
  const broadBased = operationWithLocalCommunities?.broadBased || {}
  const formationOfWorks = operationWithLocalCommunities?.formationOfWorks || {}
  const grievanceProcesses = operationWithLocalCommunities?.grievanceProcesses || {}

  // Operations with negative impacts data (413-2)
  const operationsWithNegativeImpacts = data?.operationsWithNegativeImpacts || {}
  const locationOperation = operationsWithNegativeImpacts?.locationOperation || ''
  const actualPotential = operationsWithNegativeImpacts?.actualPotential || []
  const negativeImpactsOnLocalCommunities = operationsWithNegativeImpacts?.negativeImpactsOnLocalCommunities || []

  // Supplier social assessment data (414-1, 414-2)
  const supplierScreened = data?.supplierScreened || {}
  const supplierScreenedPercentage = supplierScreened?.percentage || ''
  const supplierScreenedRemark = supplierScreened?.remark || ''
  
  const negativeSocialImpact = data?.negativeSocialImpact || {}
  const evaluated = negativeSocialImpact?.evaluated || {}
  const identifiedSuppliers = negativeSocialImpact?.identifiedSuppliers || {}
  const negativeSocialImpactsSupplyChain = negativeSocialImpact?.negativeSocialImpactsSupplyChain || {}
  const supplierAgreementRate = negativeSocialImpact?.supplierAgreementRate || {}
  const supplierTerminationRate = negativeSocialImpact?.supplierTerminationRate || {}

  // Public policy data (415)
  const publicPolicy = data?.publicPolicy || {}
  const politicalContributionsByCountry = publicPolicy?.politicalContributionsByCountry || []
  const financialPoliticalContributions = publicPolicy?.financialPoliticalContributions || ''

  // Customer health and safety data (416-1, 416-2)
  const healthAndSafetyImpactAssessment = data?.healthAndSafetyImpactAssessment || []
  
  const nonComplianceHealthSafetyIncidents = data?.nonComplianceHealthSafetyIncidents || {}
  const nonComplianceFinesPenalties = nonComplianceHealthSafetyIncidents?.nonComplianceFinesPenalties || {}
  const fineOrPenalty = nonComplianceHealthSafetyIncidents?.fineOrPenalty || {}
  const resultingWaring = nonComplianceHealthSafetyIncidents?.resultingWaring || {}
  const voluntary = nonComplianceHealthSafetyIncidents?.voluntary || {}
  const noHealthSafetyNonCompliance = nonComplianceHealthSafetyIncidents?.noHealthSafetyNonCompliance || {}

  // Marketing and labelling data (417-1, 417-2, 417-3)
  const requirmentsOfProduct = data?.requirmentsOfProduct || {}
  const productLabelingInfoRequirements = requirmentsOfProduct?.productLabelingInfoRequirements || {}
  const componentSourcingDetails = productLabelingInfoRequirements?.componentSourcingDetails || ''
  const contentImpactDetails = productLabelingInfoRequirements?.contentImpactDetails || ''
  const safeUsageGuidelines = productLabelingInfoRequirements?.safeUsageGuidelines || ''
  const disposalInstructions = productLabelingInfoRequirements?.disposalInstructions || ''
  const otherProductInfo = productLabelingInfoRequirements?.other || ''
  const complianceRateProductCategories = requirmentsOfProduct?.complianceRateProductCategories || ''
  
  const nonComplianceConcering = data?.nonComplianceConcering || {}
  const nonRegulationsProduct = nonComplianceConcering?.nonRegulations || {}
  const reportingPeriodProduct = nonRegulationsProduct?.reportingPeriod || ''
  const fineOrpenaltyProduct = nonRegulationsProduct?.fineOrpenalty || ''
  const resultingWarningProduct = nonRegulationsProduct?.resultingWarning || ''
  const voluntarCodesProduct = nonRegulationsProduct?.voluntarCodes || ''
  const noNonComplianceProductInfo = nonComplianceConcering?.noNonComplianceProductInfo || ''
  
  const nonConcerningMarketing = data?.nonConcerningMarketing || {}
  const nonRegulationsMarketing = nonConcerningMarketing?.nonRegulations || {}
  const reportingPeriodMarketing = nonRegulationsMarketing?.reportingPeriod || ''
  const fineOrpenaltyMarketing = nonRegulationsMarketing?.fineOrpenalty || ''
  const resultingWarningMarketing = nonRegulationsMarketing?.resultingWarning || ''
  const voluntarCodesMarketing = nonRegulationsMarketing?.voluntarCodes || ''
  const noNonComplianceMarketing = nonConcerningMarketing?.noNonComplianceProductInfo || ''

  // Customer privacy data (418)
  const customerPrivacy = data?.customerPrivacy || {}
  const complaintsReceived = customerPrivacy?.complaintsReceived || {}
  const regulatoryBodies = customerPrivacy?.regulatoryBodies || {}
  const customerDataIdentified = customerPrivacy?.customerDataIdentified || {}
  const noPrivacyComplaints = customerPrivacy?.noPrivacyComplaints || {}

  return (
    <>
      {/* Section 401: Material Used */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          401: MATERIAL USED
        </p>
        <h1 className={styles.mainHeading}>
          401-1 : NEW EMPLOYEE HIRES AND EMPLOYEE TURNOVER
        </h1>
        
        <div className={styles.consolidatingSection}>
        <p className={`${styles.consolidatingTitle} common_text_style`}>
          {inputData401_1a.letter} {inputData401_1a.title}
        </p>

        {/* Display tables grouped by region */}
        {processedData.length > 0 ? (
          processedData.map((regionGroup, regionIndex) => (
            <div key={regionIndex} style={{ marginBottom: '40px' }}>
              {/* Region Label - shown once per region */}
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontWeight: '400', color: '#000000', margin: '0 0 16px 0' }}>
                  Operational site: {regionGroup.region}
                </p>
              </div>

              {/* Display Male and Female tables under the same region */}
              {regionGroup.genders.map((genderData, genderIndex) => (
                <div key={genderIndex} style={{ marginBottom: '32px' }}>
                  {/* Gender Label */}
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{ fontSize: '16px', fontWeight: '600', color: '#000000', margin: '0 0 4px 0' }}>
                    • {genderData.gender}
                    </p>
                  </div>

                  {/* Table */}
                  <div className={tableStyles.tableContainer}>
                    <Table className={tableStyles.table}>
                      <TableHead className={tableStyles.headerRow}>
                        <TableRow>
                          <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                            S.No.
                          </TableCell>
                          <TableCell className={tableStyles.headerCell}>
                            Age group
                          </TableCell>
                          <TableCell className={tableStyles.headerCell}>
                            Number of new employees hired
                          </TableCell>
                          <TableCell className={tableStyles.headerCell}>
                            Rate of new hire
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {genderData.rows.map((row, rowIndex) => {
                          if (row.isTotal) {
                            return (
                          <TableRow 
                            key={rowIndex}
                                className={tableStyles.bodyRow}
                              >
                                <TableCell
                                  className={tableStyles.cell}
                                  style={{ fontWeight: '600' }}
                                  colSpan={2}
                                >
                                  Total
                            </TableCell>
                                <TableCell
                                  className={tableStyles.cell}
                                  style={{ fontWeight: '600' }}
                                  colSpan={2}
                                >
                                  {row.numberOfEmployees ?? '-'}
                                </TableCell>
                              </TableRow>
                            )
                          }

                          return (
                            <TableRow 
                              key={rowIndex}
                              className={tableStyles.bodyRow}
                            >
                              <TableCell className={tableStyles.cell}>
                                {`${row.sno}.`}
                              </TableCell>
                              <TableCell className={tableStyles.cell}>
                              {row.ageGroup || '-'}
                            </TableCell>
                              <TableCell className={tableStyles.cell}>
                              {row.numberOfEmployees !== undefined && row.numberOfEmployees !== null && row.numberOfEmployees !== '' 
                                ? row.numberOfEmployees 
                                : '-'}
                            </TableCell>
                              <TableCell className={tableStyles.cell}>
                              {row.rateOfHire !== undefined && row.rateOfHire !== null && row.rateOfHire !== '' 
                                ? row.rateOfHire 
                                : '-'}
                            </TableCell>
                          </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                   </div>
                 </div>
               ))}
             </div>
           ))
        ) : (
          <p style={{ fontSize: '16px', color: '#666666', marginTop: '16px' }}>
            -
          </p>
        )}
      </div>

      {/* Section B: Employee Turnover */}
      <div className={styles.consolidatingSection}>
        <p className={`${styles.consolidatingTitle} common_text_style`}>
          {inputData401_1b.letter} {inputData401_1b.title}
        </p>

        {/* Display tables grouped by region */}
        {turnoverData.length > 0 ? (
          turnoverData.map((regionGroup, regionIndex) => (
            <div key={regionIndex} style={{ marginBottom: '40px' }}>
              {/* Region Label - shown once per region */}
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '16px', fontWeight: '400', color: '#000000', margin: '0 0 16px 0' }}>
                  Operational site: {regionGroup.region}
                </p>
              </div>

              {/* Display Male and Female tables under the same region */}
              {regionGroup.genders.map((genderData, genderIndex) => (
                <div key={genderIndex} style={{ marginBottom: '32px' }}>
                  {/* Gender Label */}
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{ fontSize: '16px', fontWeight: '600', color: '#000000', margin: '0 0 4px 0' }}>
                    • {genderData.gender}
                    </p>
                  </div>

                  {/* Table */}
                  <div className={tableStyles.tableContainer}>
                    <Table className={tableStyles.table}>
                      <TableHead className={tableStyles.headerRow}>
                        <TableRow>
                          <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                            S.No.
                          </TableCell>
                          <TableCell className={tableStyles.headerCell}>
                            Age group
                          </TableCell>
                          <TableCell className={tableStyles.headerCell}>
                            Employee turnover
                          </TableCell>
                          <TableCell className={tableStyles.headerCell}>
                            Rate of employee turnover
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {genderData.rows.map((row, rowIndex) => {
                          if (row.isTotal) {
                            return (
                          <TableRow 
                            key={rowIndex}
                                className={tableStyles.bodyRow}
                              >
                                <TableCell
                                  className={tableStyles.cell}
                                  style={{ fontWeight: '600' }}
                                  colSpan={2}
                                >
                                  Total
                            </TableCell>
                                <TableCell
                                  className={tableStyles.cell}
                                  style={{ fontWeight: '600' }}
                                  colSpan={2}
                                >
                                  {row.turnover ?? '-'}
                                </TableCell>
                              </TableRow>
                            )
                          }

                          return (
                            <TableRow 
                              key={rowIndex}
                              className={tableStyles.bodyRow}
                            >
                              <TableCell className={tableStyles.cell}>
                                {`${row.sno}.`}
                              </TableCell>
                              <TableCell className={tableStyles.cell}>
                              {row.ageGroup || '-'}
                            </TableCell>
                              <TableCell className={tableStyles.cell}>
                              {row.turnover !== undefined && row.turnover !== null && row.turnover !== '' 
                                ? row.turnover 
                                : '-'}
                            </TableCell>
                              <TableCell className={tableStyles.cell}>
                              {row.rateOfTurnover !== undefined && row.rateOfTurnover !== null && row.rateOfTurnover !== '' 
                                ? row.rateOfTurnover 
                                : '-'}
                            </TableCell>
                          </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                   </div>
                 </div>
               ))}
             </div>
           ))
        ) : (
          <p style={{ fontSize: '16px', color: '#666666', marginTop: '16px' }}>
            -
          </p>
        )}
      </div>

      {/* Section 401-2: Benefits */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          401-2 : BENEFITS PROVIDED TO FULL-TIME EMPLOYEES THAT ARE NOT PROVIDED TO TEMPORARY OR PART-TIME EMPLOYEES
        </h1>

        {/* Question a */}
        <p className={`${styles.consolidatingTitle} common_text_style`}>
          {inputData401_2a.letter} {inputData401_2a.title}
        </p>

        {/* Benefits Table */}
        {benefitProvided && Array.isArray(benefitProvided) && benefitProvided.length > 0 ? (
          <div className={tableStyles.tableContainer} style={{ marginTop: '16px' }}>
            <Table className={tableStyles.table}>
              <TableHead className={tableStyles.headerRow}>
                <TableRow>
                  <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                    S.No.
                  </TableCell>
                  <TableCell className={tableStyles.headerCell}>
                    Benefits
                  </TableCell>
                  <TableCell className={tableStyles.headerCell}>
                    No. of Employees Benefited
                  </TableCell>
                  <TableCell className={tableStyles.headerCell}>
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {benefitProvided.map((benefit, index) => (
                  <TableRow 
                    key={index}
                    className={tableStyles.bodyRow}
                  >
                    <TableCell className={tableStyles.cell}>
                      {index + 1}.
                    </TableCell>
                    <TableCell className={tableStyles.cell}>
                      {benefit.benefit || '-'}
                    </TableCell>
                    <TableCell className={tableStyles.cell}>
                      {benefit.noOfEmployeesBenifited !== undefined && benefit.noOfEmployeesBenifited !== null && benefit.noOfEmployeesBenifited !== '' 
                        ? benefit.noOfEmployeesBenifited 
                        : '-'}
                    </TableCell>
                    <TableCell className={tableStyles.cell}>
                      {benefit.amount !== undefined && benefit.amount !== null && benefit.amount !== '' 
                        ? benefit.amount 
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p style={{ fontSize: '16px', color: '#666666', marginTop: '16px' }}>
            -
          </p>
        )}

        {/* Question b */}
        <div style={{ marginTop: '32px' }}>
          <p className={`${styles.consolidatingTitle} common_text_style`}>
            {questionData401_2b.letter} {questionData401_2b.title}
          </p>
          <div className={styles.questionsContainer}>
            <div className={styles.questionBox}>
              <p className={styles.questionAnswer}>
                {significantLocationsOfOperation || '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 401-3: Parental Leaves */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          401-3 : PARENTAL LEAVES
        </h1>

        {/* Display all questions */}
        {questionData401_3.map((question, qIndex) => {
          // Handle question e with sub-fields
          if (question.subFields) {
            return (
              <div key={qIndex} style={{ marginBottom: '32px' }}>
                <p className={`${styles.consolidatingTitle} common_text_style`}>
                  {question.letter} {question.title}
                </p>
                <div className={styles.questionsContainer}>
                  {question.subFields.map((subField, sIndex) => {
                    const dataValue = returnToWorkAndRetentionRates?.[subField.fieldName]
                    return (
                      <div key={sIndex} style={{ marginBottom: '16px' }}>
                        <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                          {subField.subHeading}
                        </p>
                        <div className={`${styles.questionsContainer} ${styles.sameLineGroup}`}>
                          <div className={styles.questionBox}>
                            <div className={styles.questionRow}>
                              <span className={styles.questionValue}>
                                Male:
                              </span>
                              <span className={`${styles.questionLabel} common_text_style`}>
                                {dataValue?.male !== undefined && dataValue?.male !== null && dataValue?.male !== '' 
                                  ? `${dataValue.male}%` 
                                  : '-'}
                              </span>
                            </div>
                          </div>
                          <div className={styles.questionBox}>
                            <div className={styles.questionRow}>
                              <span className={styles.questionValue}>
                                Female:
                              </span>
                              <span className={`${styles.questionLabel} common_text_style`}>
                                {dataValue?.female !== undefined && dataValue?.female !== null && dataValue?.female !== '' 
                                  ? `${dataValue.female}%` 
                                  : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
    </div>
            )
          }

          // Handle regular questions (a-d)
          let dataValue = {}
          if (question.fieldName === 'eligibleForParentalLeave') {
            dataValue = eligibleForParentalLeave
          } else if (question.fieldName === 'availedParentalLeave') {
            dataValue = availedParentalLeave
          } else if (question.fieldName === 'returnedAfterParentalLeave') {
            dataValue = returnedAfterParentalLeave
          } else if (question.fieldName === 'returnedWorkAfterParentalLeave') {
            dataValue = returnedWorkAfterParentalLeave
          }
          
          return (
            <div key={qIndex} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div className={`${styles.questionsContainer} ${styles.sameLineGroup}`}>
                <div className={styles.questionBox}>
                  <div className={styles.questionRow}>
                    <span className={styles.questionValue}>
                      Male:
                    </span>
                    <span className={`${styles.questionLabel} common_text_style`}>
                      {dataValue?.male !== undefined && dataValue?.male !== null && dataValue?.male !== '' 
                        ? dataValue.male 
                        : '-'}
                    </span>
                  </div>
                </div>
                <div className={styles.questionBox}>
                  <div className={styles.questionRow}>
                    <span className={styles.questionValue}>
                      Female:
                    </span>
                    <span className={`${styles.questionLabel} common_text_style`}>
                      {dataValue?.female !== undefined && dataValue?.female !== null && dataValue?.female !== '' 
                        ? dataValue.female 
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      </div>

      {/* Section 402: Labour Management Relations */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          402 : LABOUR MANAGEMENT RELATIONS
        </p>
        <h1 className={styles.mainHeading}>
          402-1 : MINIMUM NOTICE PERIOD REGARDING OPERATIONAL CHANGES
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Display questions */}
        {questionData402_1.map((question, qIndex) => {
          if (question.fieldName === 'noticePeriodDays') {
            return (
              <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
                <p className={`${styles.questionTitle} common_text_style`}>
                  {question.letter} {question.title}
                  {noticePeriodDays !== undefined && noticePeriodDays !== null && noticePeriodDays !== '' ? (
                    <span style={{ marginLeft: '8px' }}>{noticePeriodDays}</span>
                  ) : (
                    <span style={{ marginLeft: '8px' }}>-</span>
                  )}
                </p>
              </div>
            )
          } else if (question.fieldName === 'collectiveBargaining') {
            const answer = hasCollectiveBargaining 
              ? (collectiveBargainingDays !== undefined && collectiveBargainingDays !== null && collectiveBargainingDays !== '' ? `Yes, ${collectiveBargainingDays}` : 'Yes')
              : 'No'
            
            return (
              <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
                <p className={`${styles.questionTitle} common_text_style`}>
                  {question.letter} {question.title}
                  <span style={{ marginLeft: '8px' }}>{answer}</span>
                </p>
              </div>
            )
          }
          return null
        })}
        </div>
      </div>

      {/* Section 403: Occupational Health and Safety */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          403 : OCCUPATIONAL HEALTH AND SAFETY
        </p>
        <h1 className={styles.mainHeading}>
          403-1 : OCCUPATIONAL HEALTH AND SAFETY MANAGEMENT SYSTEM
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Display questions */}
        {questionData403_1.map((question, qIndex) => {
          let dataValue = {}
          if (question.fieldName === 'occupationalHealthDetails') {
            dataValue = occupationalHealthDetails
          } else if (question.fieldName === 'coverageProvided') {
            dataValue = coverageProvided
          }
          
          const status = dataValue?.status
          const remarks = dataValue?.remarks || ''
          
          return (
            <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p style={{ fontWeight: 'normal', marginBottom: '8px' }}>
                  {status ? (
                    remarks ? `Yes, ${remarks}` : 'Yes'
                  ) : (
                    'No'
                  )}
                </p>
                
                {/* Display sub-questions for question a */}
                {question.subQuestions && status && question.subQuestions.map((subQ, sIndex) => {
                  let subDataValue = {}
                  if (subQ.fieldName === 'legalRequirements') {
                    subDataValue = legalRequirements
                  } else if (subQ.fieldName === 'riskIdentification') {
                    subDataValue = riskIdentification
                  }
                  
                  const subStatus = subDataValue?.status
                  const subRemarks = subDataValue?.remarks || ''
                  
                  return (
                    <div key={sIndex} style={{ marginTop: '16px', marginLeft: '16px' }}>
                      <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '4px' }}>
                        {subQ.number} {subQ.title}
                      </p>
                      <p style={{ fontWeight: 'normal', marginLeft: '0' }}>
                        {subStatus ? (
                          subRemarks ? `Yes, ${subRemarks}` : 'Yes'
                        ) : (
                          'No'
                        )}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Section 403-2: Hazard Identification, Risk Assessment, and Incident Investigation */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-2 : HAZARD IDENTIFICATION, RISK ASSESSMENT, AND INCIDENT INVESTIGATION
        </h1>

        {/* Display questions */}
        {questionData403_2.map((question, qIndex) => {
          // Handle question a with sub-questions
          if (question.subQuestions && question.fieldName === 'detailedOverview') {
            return (
              <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
                <p className={`${styles.questionTitle} common_text_style`}>
                  {question.letter} {question.title}
                </p>
                <div style={{ marginTop: '8px', marginLeft: '0' }}>
                  <p className={styles.questionAnswer} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
                    {procedureEmployed || '-'}
                  </p>
                  
                  {/* Display sub-questions */}
                  {question.subQuestions.map((subQ, sIndex) => {
                    let subAnswer = ''
                    if (subQ.fieldName === 'ensuredEffectiveness') {
                      subAnswer = ensuredEffectiveness
                    } else if (subQ.fieldName === 'outcome') {
                      subAnswer = outcome
                    }
                    
                    return (
                      <div key={sIndex} style={{ marginTop: '16px', marginLeft: '16px' }}>
                        <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '4px' }}>
                          {subQ.number} {subQ.title}
                        </p>
                        <p className={styles.questionAnswer} style={{ fontWeight: 'normal', marginLeft: '0' }}>
                          {subAnswer || '-'}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          }

          // Handle regular questions (b, c, d)
          let answer = ''
          if (question.fieldName === 'workerHazardReportingMechanisms') {
            answer = workerHazardReportingMechanisms
          } else if (question.fieldName === 'workerSafetyWithdrawalPolicies') {
            answer = workerSafetyWithdrawalPolicies
          } else if (question.fieldName === 'incidentInvestigationProcedures') {
            answer = incidentInvestigationProcedures
          }

          return (
            <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {answer || '-'}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Section 403-3: Occupational Health Services */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-3 : OCCUPATIONAL HEALTH SERVICES
        </h1>

        {questionData403_3.map((question, qIndex) => {
          return (
            <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
                  {occupationalHealthServicesFunctions || '-'}
                </p>
                
                {/* Display sub-questions */}
                {question.subQuestions && question.subQuestions.map((subQ, sIndex) => {
                  let subAnswer = ''
                  if (subQ.fieldName === 'workerHealthInfoConfidentiality') {
                    subAnswer = workerHealthInfoConfidentiality
                  } else if (subQ.fieldName === 'workerHealthInfoUsageProtection') {
                    subAnswer = workerHealthInfoUsageProtection
                  }
                  
                  return (
                    <div key={sIndex} style={{ marginTop: '16px', marginLeft: '16px' }}>
                      <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '4px' }}>
                        {subQ.number} {subQ.title}
                      </p>
                      <p className={styles.questionAnswer} style={{ fontWeight: 'normal', marginLeft: '0' }}>
                        {subAnswer || '-'}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Section 403-4: Worker Participation, Consultation, and Communication */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-4 : WORKER PARTICIPATION, CONSULTATION, AND COMMUNICATION ON OCCUPATIONAL HEALTH AND SAFETY
        </h1>

        {questionData403_4.map((question, qIndex) => {
          let answer = ''
          if (question.fieldName === 'workerPaticipation') {
            answer = workerPaticipation
          } else if (question.fieldName === 'healthSafetyCommitteeDetails') {
            answer = healthSafetyCommitteeDetails
          }

          return (
            <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {answer || '-'}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Section 403-5: Worker Training on Occupational Health and Safety */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-5 : WORKER TRAINING ON OCCUPATIONAL HEALTH AND SAFETY
        </h1>

        {questionData403_5.map((question, qIndex) => {
          return (
            <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {workerOHSTraining || '-'}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Section 403-6: Promotion of Worker Health */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-6 : PROMOTION OF WORKER HEALTH
        </h1>

        {questionData403_6.map((question, qIndex) => {
          if (question.subQuestions && question.fieldName === 'voluntaryHealthPromotionPrograms') {
            return (
              <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
                <p className={`${styles.questionTitle} common_text_style`}>
                  {question.letter} {question.title}
                </p>
                <div style={{ marginTop: '8px', marginLeft: '0' }}>
                  <p className={styles.questionAnswer} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
                    {voluntaryHealthPromotionPrograms || '-'}
                  </p>
                  
                  {/* Display sub-questions */}
                  {question.subQuestions.map((subQ, sIndex) => {
                    let subAnswer = ''
                    if (subQ.fieldName === 'healthInformationConfidentialityMeasures') {
                      subAnswer = healthInformationConfidentialityMeasures
                    } else if (subQ.fieldName === 'healthInfoPrivacyNonDiscrimination') {
                      subAnswer = healthInfoPrivacyNonDiscrimination
                    }
                    
                    return (
                      <div key={sIndex} style={{ marginTop: '16px', marginLeft: '16px' }}>
                        <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '4px' }}>
                          {subQ.number} {subQ.title}
                        </p>
                        <p className={styles.questionAnswer} style={{ fontWeight: 'normal', marginLeft: '0' }}>
                          {subAnswer || '-'}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          }

          return (
            <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {workerHealthcareAccess || '-'}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Section 403-7: Prevention and Mitigation of OHS Impacts */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-7 : PREVENTION AND MITIGATION OF OCCUPATIONAL HEALTH AND SAFETY IMPACTS DIRECTLY LINKED BY BUSINESS RELATIONSHIPS
        </h1>

        {questionData403_7.map((question, qIndex) => {
          return (
            <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {occupationalHealthPreventionMitigation || '-'}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Section 403-8: Workers Covered by OHS Management System */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-8 : WORKERS COVERED BY AN OCCUPATIONAL HEALTH AND SAFETY MANAGEMENT SYSTEM
        </h1>

        {questionData403_8.map((question, qIndex) => {
          if (question.subQuestions && question.fieldName === 'OHSManagementSystem') {
            return (
              <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
                <p className={`${styles.questionTitle} common_text_style`}>
                  {question.letter} {question.title}
                </p>
                <div style={{ marginTop: '8px', marginLeft: '0' }}>
                  <p className={styles.questionAnswer} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
                    {OHSManagementSystemAlignmentDetails || '-'}
                  </p>
                  
                  {/* Display sub-questions */}
                  {question.subQuestions.map((subQ, sIndex) => {
                    let subAnswer = ''
                    if (subQ.fieldName === 'OHSSystemCoverageDetails') {
                      subAnswer = OHSSystemCoverageDetails
                    } else if (subQ.fieldName === 'OHSSystemInternalAuditCoverage') {
                      subAnswer = OHSSystemInternalAuditCoverage
                    } else if (subQ.fieldName === 'WorkerExclusionReasons') {
                      subAnswer = WorkerExclusionReasons
                    }
                    
                    return (
                      <div key={sIndex} style={{ marginTop: '16px', marginLeft: '16px' }}>
                        <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '4px' }}>
                          {subQ.number} {subQ.title}
                        </p>
                        <p className={styles.questionAnswer} style={{ fontWeight: 'normal', marginLeft: '0' }}>
                          {subAnswer || '-'}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          }

          let answer = ''
          if (question.fieldName === 'excludedWorkers') {
            answer = excludedWorkers
          } else if (question.fieldName === 'contextualInformation') {
            answer = contextualInformation
          }

          return (
            <div key={qIndex} className={styles.questionBox} style={{ marginBottom: '24px' }}>
              <p className={`${styles.questionTitle} common_text_style`}>
                {question.letter} {question.title}
              </p>
              <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {answer || '-'}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Section 403-9: Work-related Injuries */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-9 : WORK-RELATED INJURIES
        </h1>

        {/* Part a: For all employees */}
        <div style={{ marginBottom: '32px' }}>
          <p className={`${styles.consolidatingTitle} common_text_style`} style={{ marginBottom: '16px' }}>
            a. For all employees
          </p>

          {/* 1. Fatalities */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              1. The number and rate of fatalities as a result of work-related injury.
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No. of fatalities:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeFatalities !== undefined && employeeFatalities !== null && employeeFatalities !== '' ? employeeFatalities : '-'}
                </span>
              </div>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Rate of Fatalities:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeRateOfFatalities !== undefined && employeeRateOfFatalities !== null && employeeRateOfFatalities !== '' ? employeeRateOfFatalities : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeRemarkFatalities || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 2. High-consequence injuries */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              2. The number and rate of high-consequence work-related injuries (excluding fatalities).
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No. of high-consequence work-related injuries:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeHighConsequenceInjuries !== undefined && employeeHighConsequenceInjuries !== null && employeeHighConsequenceInjuries !== '' ? employeeHighConsequenceInjuries : '-'}
                </span>
              </div>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Rate of high-consequence work-related injuries:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeRateOfHighConsequenceInjuries !== undefined && employeeRateOfHighConsequenceInjuries !== null && employeeRateOfHighConsequenceInjuries !== '' ? employeeRateOfHighConsequenceInjuries : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeRemarkHighConsequence || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 3. Recordable injuries */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              3. The number and rate of recordable work-related injuries.
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No. of recordable work-related injuries:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeRecordableInjuries !== undefined && employeeRecordableInjuries !== null && employeeRecordableInjuries !== '' ? employeeRecordableInjuries : '-'}
                </span>
              </div>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Rate of recordable work-related injuries:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeRateOfRecordableInjuries !== undefined && employeeRateOfRecordableInjuries !== null && employeeRateOfRecordableInjuries !== '' ? employeeRateOfRecordableInjuries : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeRemarkRecordable || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 4. Main types of work-related injury - Table */}
          <div style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              4. The main types of work-related injury.
            </p>
            {employeeWorkRelatedInjuryTypes && Array.isArray(employeeWorkRelatedInjuryTypes) && employeeWorkRelatedInjuryTypes.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Type of work-related injury
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeWorkRelatedInjuryTypes.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.workRelatedInjury || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>

          {/* 5. Hours worked */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              5. No of Hours Worked (in Hrs)
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No of hours worked:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeNoOfHourWorked !== undefined && employeeNoOfHourWorked !== null && employeeNoOfHourWorked !== '' ? employeeNoOfHourWorked : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {employeeRemarkHoured || '-'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Part b: For all workers who are not employees */}
        <div style={{ marginBottom: '32px' }}>
          <p className={`${styles.consolidatingTitle} common_text_style`} style={{ marginBottom: '16px' }}>
            b. For all workers who are not employees but whose work and/or workplace is controlled by the organization
          </p>

          {/* 1. Fatalities */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              1. The number and rate of fatalities as a result of work-related injury
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No. of fatalities:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceFatalities !== undefined && workplaceFatalities !== null && workplaceFatalities !== '' ? workplaceFatalities : '-'}
                </span>
              </div>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Rate of Fatalities:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceRateOfFatalities !== undefined && workplaceRateOfFatalities !== null && workplaceRateOfFatalities !== '' ? workplaceRateOfFatalities : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceRemarkFatalities || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 2. High-consequence injuries */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              2. The number and rate of high-consequence work-related injuries (excluding fatalities)
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No. of high-consequence work-related injuries:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceHighConsequenceInjuries !== undefined && workplaceHighConsequenceInjuries !== null && workplaceHighConsequenceInjuries !== '' ? workplaceHighConsequenceInjuries : '-'}
                </span>
              </div>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Rate of high-consequence work-related injuries:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceRateOfHighConsequenceInjuries !== undefined && workplaceRateOfHighConsequenceInjuries !== null && workplaceRateOfHighConsequenceInjuries !== '' ? workplaceRateOfHighConsequenceInjuries : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceRemarkHighConsequence || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 3. Recordable injuries */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              3. The number and rate of recordable work-related injuries
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No. of recordable work-related injuries:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceRecordableInjuries !== undefined && workplaceRecordableInjuries !== null && workplaceRecordableInjuries !== '' ? workplaceRecordableInjuries : '-'}
                </span>
              </div>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Rate of recordable work-related injuries:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceRateOfRecordableInjuries !== undefined && workplaceRateOfRecordableInjuries !== null && workplaceRateOfRecordableInjuries !== '' ? workplaceRateOfRecordableInjuries : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceRemarkRecordable || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 4. Main types of work-related injury - Table */}
          <div style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              4. The main types of work-related injury.
            </p>
            {workplaceWorkRelatedInjuryTypes && Array.isArray(workplaceWorkRelatedInjuryTypes) && workplaceWorkRelatedInjuryTypes.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Type of work-related injury
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workplaceWorkRelatedInjuryTypes.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.workRelatedInjury || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>

          {/* 5. Hours worked */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              5. No of Hours Worked (in Hrs)
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No of hours worked:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceNoOfHourWorked !== undefined && workplaceNoOfHourWorked !== null && workplaceNoOfHourWorked !== '' ? workplaceNoOfHourWorked : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {workplaceRemarkHoured || '-'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Part c: Work-related hazards */}
        <div style={{ marginBottom: '32px' }}>
          <p className={`${styles.consolidatingTitle} common_text_style`} style={{ marginBottom: '16px' }}>
            c. The work-related hazards that pose a risk of high-consequence injury including:
          </p>

          {/* i. How hazards determined */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              1. How these hazards have been determined
            </p>
            <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {hazardsDetermined || '-'}
                </p>
            </div>
          </div>

          {/* ii. Which hazards caused injuries */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              2. Which of these hazards have caused or contributed to high-consequence injuries during the reporting period
            </p>
            <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {hazardsHighConsequence || '-'}
                </p>
            </div>
          </div>

          {/* iii. Actions taken - Table */}
          <div style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              3. Actions taken or underway to eliminate these hazards and minimize risks using the hierarchy of controls
            </p>
            {hazardsActions && Array.isArray(hazardsActions) && hazardsActions.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hazardsActions.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.action || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>
        </div>

        {/* Part d: Other actions */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            d. Any actions taken or underway to eliminate other work-related hazards and minimize risks using the hierarchy of controls.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {hazardsAndMinizeRisk || '-'}
            </p>
          </div>
        </div>

        {/* Part e: Whether rates calculated */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            e. Whether the rates have been calculated based on 200,000 or 1,000,000 hours worked.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p style={{ fontWeight: 'normal' }}>
              {wheatherTheRatesCalulated ? 'Yes' : 'No'}
            </p>
          </div>
        </div>

        {/* Part f: Whether workers excluded */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            f. Whether and, if so, why any workers have been excluded from this disclosure, including the types of worker excluded.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p style={{ fontWeight: 'normal' }}>
              {workerExcluded ? 'Yes' : 'No'}
            </p>
          </div>
        </div>

        {/* Part g: Contextual information */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            g. Any contextual information necessary to understand how the data have been compiled, such as any standards, methodologies, and assumptions used.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {workRelatedContextualInformation || '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 403-10: Work-related Ill Health */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          403-10 : WORK-RELATED ILL HEALTH
        </h1>

        {/* Part a: For all employees */}
        <div style={{ marginBottom: '32px' }}>
          <p className={`${styles.consolidatingTitle} common_text_style`} style={{ marginBottom: '16px' }}>
            a. For all employees
          </p>

          {/* 1. Fatalities */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              1. The number of fatalities as a result of work-related ill health:
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No of fatalities as a result of work-related ill health:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {illHealthEmployeeFatalities !== undefined && illHealthEmployeeFatalities !== null && illHealthEmployeeFatalities !== '' ? illHealthEmployeeFatalities : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {illHealthEmployeeRemarkFatalities || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 2. Recordable cases */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              2. The number of cases of recordable work-related ill health:
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No of cases of recordable work-related ill health:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {illHealthEmployeeRecordable !== undefined && illHealthEmployeeRecordable !== null && illHealthEmployeeRecordable !== '' ? illHealthEmployeeRecordable : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {illHealthEmployeeRemarkRecordable || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 3. Main types of work-related ill health - Table */}
          <div style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              3. The main types of work-related ill health:
            </p>
            {illHealthEmployeeTypes && Array.isArray(illHealthEmployeeTypes) && illHealthEmployeeTypes.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Type of work-related ill health
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {illHealthEmployeeTypes.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.health || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>
        </div>

        {/* Part b: For all workers who are not employees */}
        <div style={{ marginBottom: '32px' }}>
          <p className={`${styles.consolidatingTitle} common_text_style`} style={{ marginBottom: '16px' }}>
            b. For all workers who are not employees but whose work and/or workplace is controlled by the organization
          </p>

          {/* 1. Fatalities */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              1. The number of fatalities as a result of work-related ill health:
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No of fatalities as a result of work-related ill health:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {illHealthWorkplaceFatalities !== undefined && illHealthWorkplaceFatalities !== null && illHealthWorkplaceFatalities !== '' ? illHealthWorkplaceFatalities : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {illHealthWorkplaceRemarkFatalities || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 2. Recordable cases */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              2. The number of cases of recordable work-related ill health:
            </p>
            <div className={styles.questionsContainer}>
              <div className={styles.questionRow} style={{ marginBottom: '4px' }}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>No of cases of recordable work-related ill health:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {illHealthWorkplaceRecordable !== undefined && illHealthWorkplaceRecordable !== null && illHealthWorkplaceRecordable !== '' ? illHealthWorkplaceRecordable : '-'}
                </span>
              </div>
              <div className={styles.questionRow}>
                <span className={styles.questionLabel} style={{ fontWeight: 'normal' }}>Remark:</span>
                <span className={styles.questionValue} style={{ fontWeight: 'normal' }}>
                  {illHealthWorkplaceRemarkRecordable || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* 3. Main types of work-related ill health - Table */}
          <div style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              3. The main types of work-related ill health:
            </p>
            {illHealthWorkplaceTypes && Array.isArray(illHealthWorkplaceTypes) && illHealthWorkplaceTypes.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Type of work-related ill health
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {illHealthWorkplaceTypes.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.health || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>
        </div>

        {/* Part c: Work-related hazards */}
        <div style={{ marginBottom: '32px' }}>
          <p className={`${styles.consolidatingTitle} common_text_style`} style={{ marginBottom: '16px' }}>
            c. The work-related hazards that pose a risk of ill health, including:
          </p>

          {/* i. How hazards determined */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              1. How does these hazards have been determined
            </p>
            <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {illHealthHazardsDetermined || '-'}
                </p>
            </div>
          </div>

          {/* ii. Which hazards caused ill health */}
          <div className={styles.questionBox} style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              2. Which of these hazards have caused or contributed to cases of ill health during the reporting period
            </p>
            <div style={{ marginTop: '8px', marginLeft: '0' }}>
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  {illHealthHazardsHighConsequence || '-'}
                </p>
            </div>
          </div>

          {/* iii. Actions taken - Table */}
          <div style={{ marginBottom: '16px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              3. Actions taken or underway to eliminate these hazards and minimize risks using the hierarchy of controls.
            </p>
            {illHealthHazardsActions && Array.isArray(illHealthHazardsActions) && illHealthHazardsActions.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {illHealthHazardsActions.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.action || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>
        </div>

        {/* Part d: Other actions */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            d. Whether and, if so, why any workers have been excluded from this disclosure, including the types of worker excluded.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {illHealthHazardsAndMinizeRisk || '-'}
            </p>
          </div>
        </div>

        {/* Part e: Contextual information */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            e. Any contextual information necessary to understand how the data have been compiled, such as any standards, methodologies, and assumptions used.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {illHealthContextualInformation || '-'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 404: Training and Education */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          404 : TRAINING AND EDUCATION
        </p>
        <h1 className={styles.mainHeading}>
          404-1 : AVERAGE HOURS OF TRAINING PER YEAR PER EMPLOYEE
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Average number of training hours */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            a. What is the average number of training hours completed by employees of the organization during the reporting period? Please provide the breakdown:
          </p>

          {/* Table 1: Breakdown by Employee Category and Gender */}
          <div style={{ marginBottom: '24px' }}>
            <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
              <Table className={tableStyles.table}>
                <TableHead className={tableStyles.headerRow}>
                  <TableRow>
                    <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                      S.No.
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Employee category
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Male employees training hours
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Female employees training hours
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trainingCategory && Array.isArray(trainingCategory) && trainingCategory.length > 0 ? (
                    <>
                      {trainingCategory.map((item, index) => (
                        <TableRow 
                          key={index}
                          className={tableStyles.bodyRow}
                        >
                          <TableCell className={tableStyles.cell}>
                            {index + 1}.
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.categories || '-'}
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.male !== undefined && item.male !== null && item.male !== '' ? item.male : '-'}
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.female !== undefined && item.female !== null && item.female !== '' ? item.female : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total row */}
                      {(() => {
                        const maleTotal = trainingCategory.reduce((sum, item) => sum + (parseInt(item.male) || 0), 0)
                        const femaleTotal = trainingCategory.reduce((sum, item) => sum + (parseInt(item.female) || 0), 0)
                        return (
                          <TableRow className={tableStyles.bodyRow}>
                            <TableCell className={tableStyles.cell} colSpan={2} style={{ fontWeight: '600' }}>
                              Total hours
                            </TableCell>
                            <TableCell className={tableStyles.cell} style={{ fontWeight: '600' }}>
                              {maleTotal}
                            </TableCell>
                            <TableCell className={tableStyles.cell} style={{ fontWeight: '600' }}>
                              {femaleTotal}
                            </TableCell>
                          </TableRow>
                        )
                      })()}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className={tableStyles.cell} style={{ textAlign: 'center' }}>
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Table 2: Breakdown by Age Group */}
          <div style={{ marginBottom: '16px' }}>
            <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
              <Table className={tableStyles.table}>
                <TableHead className={tableStyles.headerRow}>
                  <TableRow>
                    <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                      S.No.
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Age Group
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Training hours
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Remark
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trainingGroups && Array.isArray(trainingGroups) && trainingGroups.length > 0 ? (
                    <>
                      {trainingGroups.map((item, index) => (
                        <TableRow 
                          key={index}
                          className={tableStyles.bodyRow}
                        >
                          <TableCell className={tableStyles.cell}>
                            {index + 1}.
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.ageGroup || '-'}
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.hours !== undefined && item.hours !== null && item.hours !== '' ? item.hours : '-'}
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.remark || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total row */}
                      {(() => {
                        const hoursTotal = trainingGroups.reduce((sum, item) => sum + (parseInt(item.hours) || 0), 0)
                        return (
                          <TableRow className={tableStyles.bodyRow}>
                            <TableCell className={tableStyles.cell} colSpan={2} style={{ fontWeight: '600' }}>
                              Total hours
                            </TableCell>
                            <TableCell className={tableStyles.cell} style={{ fontWeight: '600' }}>
                              {hoursTotal}
                            </TableCell>
                            <TableCell className={tableStyles.cell}>
                              -
                            </TableCell>
                          </TableRow>
                        )
                      })()}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className={tableStyles.cell} style={{ textAlign: 'center' }}>
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Section 404-2: Programs for upgrading employee skills and transition assistance programs */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          404-2 : PROGRAMS FOR UPGRADING EMPLOYEE SKILLS AND TRANSITION ASSISTANCE PROGRAMS
        </h1>

        {/* Part a: Programs implemented */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. Describe the programs implemented and the support provided to enhance employee skills? Please specify the type and scope of these programs.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {skillEnhancementInitiatives || '-'}
            </p>
          </div>
        </div>

        {/* Part b: Transition assistance programs */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. Outline the transition assistance programs offered to support ongoing employability and manage career transitions resulting from retirement or termination of employment?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {careerTransitionPrograms || '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 404-3: Percentage of employees receiving regular performance and career development reviews */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          404-3 : PERCENTAGE OF EMPLOYEES RECEIVING REGULAR PERFORMANCE AND CAREER DEVELOPMENT REVIEWS
        </h1>

        {/* Part a: Percentage by gender and employee category */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            a. What percentage of the total workforce, categorized by gender and employee category, participated in regular performance and career development reviews during the reporting period?
          </p>

          {/* Table: Performance and career development reviews */}
          <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
            <Table className={tableStyles.table}>
              <TableHead className={tableStyles.headerRow}>
                <TableRow>
                  <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                    S.No.
                  </TableCell>
                  <TableCell className={tableStyles.headerCell}>
                    Employee category
                  </TableCell>
                  <TableCell className={tableStyles.headerCell}>
                    Percentage of male employees participated in regular performance and career development
                  </TableCell>
                  <TableCell className={tableStyles.headerCell}>
                    Percentage of female employees participated in regular performance and career development
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {performanceAndCareerDevelopment && Array.isArray(performanceAndCareerDevelopment) && performanceAndCareerDevelopment.length > 0 ? (
                  performanceAndCareerDevelopment.map((item, index) => (
                    <TableRow 
                      key={index}
                      className={tableStyles.bodyRow}
                    >
                      <TableCell className={tableStyles.cell}>
                        {index + 1}.
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.category || '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.malePercentage !== undefined && item.malePercentage !== null && item.malePercentage !== '' ? `${item.malePercentage}%` : '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.femalePercentage !== undefined && item.femalePercentage !== null && item.femalePercentage !== '' ? `${item.femalePercentage}%` : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className={tableStyles.cell} style={{ textAlign: 'center' }}>
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        </div>
      </div>

      {/* Section 405: Diversity and Equal Opportunity */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          405 : DIVERSITY AND EQUAL OPPORTUNITY
        </p>
        <h1 className={styles.mainHeading}>
          405-1 : DIVERSITY OF GOVERNANCE BODIES AND EMPLOYEES
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Governance bodies diversity */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            a. What percentage of individuals serving within the organization's governance bodies fall into each of the following diversity categories?
          </p>

          {/* Table 1: Gender */}
          <div style={{ marginBottom: '24px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              1. Gender
            </p>
            {governanceGender && Array.isArray(governanceGender) && governanceGender.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Gender
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        %
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Remark
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {governanceGender.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.gender || '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.percentage !== undefined && item.percentage !== null && item.percentage !== '' ? `${item.percentage}%` : '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.remark || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>

          {/* Table 2: Age Groups */}
          <div style={{ marginBottom: '24px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              2. Age Groups
            </p>
            {governanceAgeGroups && Array.isArray(governanceAgeGroups) && governanceAgeGroups.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Age Group
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        %
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Remark
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {governanceAgeGroups.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.ageGroup || '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.percentage !== undefined && item.percentage !== null && item.percentage !== '' ? `${item.percentage}%` : '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.remark || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>

          {/* Table 3: Other relevant diversity indicators */}
          <div style={{ marginBottom: '24px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              3. Other relevant diversity indicators, such as minority or vulnerable groups.
            </p>
            {governanceOther && Array.isArray(governanceOther) && governanceOther.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Indicator
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        %
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Remark
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {governanceOther.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.ageGroup || '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.percentage !== undefined && item.percentage !== null && item.percentage !== '' ? `${item.percentage}%` : '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.remark || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>
        </div>

        {/* Part b: Employee diversity */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            b. What percentage of employees within each employee category represent the following diversity categories?
          </p>

          {/* Table 1: Gender */}
          <div style={{ marginBottom: '24px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              1. Gender
            </p>
            {employeeGender && Array.isArray(employeeGender) && employeeGender.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Employee Category
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Male %
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Female %
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeGender.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.employeeCategory || '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.male !== undefined && item.male !== null && item.male !== '' ? `${item.male}%` : '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.female !== undefined && item.female !== null && item.female !== '' ? `${item.female}%` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>

          {/* Table 2: Age Groups */}
          <div style={{ marginBottom: '24px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              2. Age Groups
            </p>
            {employeeAgeGroups && Array.isArray(employeeAgeGroups) && employeeAgeGroups.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Employee category
                      </TableCell>
                      <TableCell className={tableStyles.headerCell} colSpan={3}>
                        Percentage of employee in age groups
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell}></TableCell>
                      <TableCell className={tableStyles.headerCell}></TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Under 30 years old
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        30-50 years
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Over 50 years
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeAgeGroups.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.employeeCategory || '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.underThirtyYrs !== undefined && item.underThirtyYrs !== null && item.underThirtyYrs !== '' ? `${item.underThirtyYrs}%` : '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.thirtytoFiftyYears !== undefined && item.thirtytoFiftyYears !== null && item.thirtytoFiftyYears !== '' ? `${item.thirtytoFiftyYears}%` : '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.overFiftyYrs !== undefined && item.overFiftyYrs !== null && item.overFiftyYrs !== '' ? `${item.overFiftyYrs}%` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>

          {/* Table 3: Other relevant diversity indicators */}
          <div style={{ marginBottom: '24px', marginLeft: '16px' }}>
            <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
              3. Other relevant diversity indicators, such as minority or vulnerable groups.
            </p>
            {employeeOther && Array.isArray(employeeOther) && employeeOther.length > 0 ? (
              <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                <Table className={tableStyles.table}>
                  <TableHead className={tableStyles.headerRow}>
                    <TableRow>
                      <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                        S.No.
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Employee Category
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Minority Group
                      </TableCell>
                      <TableCell className={tableStyles.headerCell}>
                        Vulnerable Group
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeOther.map((item, index) => (
                      <TableRow 
                        key={index}
                        className={tableStyles.bodyRow}
                      >
                        <TableCell className={tableStyles.cell}>
                          {index + 1}.
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.employeeCategory || '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.minorityGroup !== undefined && item.minorityGroup !== null && item.minorityGroup !== '' ? item.minorityGroup : '-'}
                        </TableCell>
                        <TableCell className={tableStyles.cell}>
                          {item.vulnarableGroup !== undefined && item.vulnarableGroup !== null && item.vulnarableGroup !== '' ? item.vulnarableGroup : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
                No data available
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section 405-2: Ratio of basic salary and remuneration of women to men */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          405-2 : RATIO OF BASIC SALARY AND REMUNERATION OF WOMEN TO MEN
        </h1>

        {/* Part a: Ratio by employee category */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            a. What is the ratio of basic salary and remuneration between women and men within each employee category, categorized by significant locations of operation?
          </p>

          {/* Table: Male to female remuneration ratio */}
          <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
            <Table className={tableStyles.table}>
              <TableHead className={tableStyles.headerRow}>
                <TableRow>
                  <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                    S.No.
                  </TableCell>
                  <TableCell className={tableStyles.headerCell}>
                    Employee Category
                  </TableCell>
                  <TableCell className={tableStyles.headerCell}>
                    Male to female remuneration ratio
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {manWomenSalaryRatio && Array.isArray(manWomenSalaryRatio) && manWomenSalaryRatio.length > 0 ? (
                  manWomenSalaryRatio.map((item, index) => (
                    <TableRow 
                      key={index}
                      className={tableStyles.bodyRow}
                    >
                      <TableCell className={tableStyles.cell}>
                        {index + 1}.
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.employeeCategory || '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.ratio || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className={tableStyles.cell} style={{ textAlign: 'center' }}>
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Part b: Significant locations definition */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. How does the organization define 'significant locations of operation' for reporting purposes?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {significantLocationsOfOperationSalary || '-'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 406: Non Discrimination */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          406 : NON DISCRIMINATION
        </p>
        <h1 className={styles.mainHeading}>
          406-1 : INCIDENTS OF DISCRIMINATION AND CORRECTIVE ACTIONS TAKEN
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Number of incidents */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. Indicate number of incidents of discrimination occurred during the reporting period?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {discriminationNumber !== undefined && discriminationNumber !== null && discriminationNumber !== '' 
                ? (discriminationNumber === '0' || discriminationNumber === 0 
                    ? 'No incidents reported' 
                    : `${discriminationNumber}${discriminationRemark ? ` - ${discriminationRemark}` : ''}`)
                : (discriminationRemark || 'No incidents reported')}
            </p>
          </div>
        </div>

        {/* Part b: Current status and actions */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. What is the current status of the discrimination incidents, and what actions have been taken? Please indicate:
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {discriminationIncidentStatusActions || '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Whether the incident has been reviewed by the organization.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {incidentReviewed || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. If remediation plans are being implemented.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {remediationPlansImplemented || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                3. Whether remediation plans have been implemented, and if so, provide details of the results reviewed through routine internal management review processes.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {remediationPlansDetails || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                4. If the incident is no longer subject to action.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {noLongerSubject || '-'}
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Section 407: Freedom of Association */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          407 : FREEDOM OF ASSOCIATION
        </p>
        <h1 className={styles.mainHeading}>
          407-1 : OPERATIONS AND SUPPLIERS IN WHICH THE RIGHT TO FREEDOM OF ASSOCIATION AND COLLECTIVE BARGAINING MAY BE AT RISK
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Identify operations and suppliers */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. Can you identify operations and suppliers where there is a risk of workers' rights to exercise freedom of association or collective bargaining being violated or significantly at risk? Please specify:
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {atRiskSuppliers || '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. The type of operation and supplier.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {operationAndSupplierType || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Countries or geographic areas where operations and suppliers are considered at risk.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {atRiskCountriesOrAreas || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Part b: Actions taken */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. What actions did the organization take during the reporting period to support the rights of workers to exercise freedom of association and collective bargaining?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {collectiveBargaining || '-'}
              </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 408: Child Labour */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          408 : CHILD LABOUR
        </p>
        <h1 className={styles.mainHeading}>
          408-1 : OPERATIONS AND INCIDENTS AT SIGNIFICANT RISK FOR INCIDENTS OF CHILD LABOR
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Identify operations and suppliers */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. Identify operations and suppliers where there is a significant risk of incidents involving:
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {childLabourIncidents || '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Child Labor
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {childLabour || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Young workers exposed to hazardous work
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {youngWorkers || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Part b: Identify operations and suppliers with significant risk */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. Identify operations and suppliers with a significant risk of incidents involving child labor? Please specify:
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {childLabors || '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. The type of operation (e.g., manufacturing plant) and supplier.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {operationTypeAndSupplier || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Countries or geographic areas where operations and suppliers are considered at risk.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {countriesOrRegionsAtRisk || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Part c: Measures to abolish child labor */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            c. What measures did the organization undertake during the reporting period to contribute to the effective abolition of child labor?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {measuresToAbolishChildLabor || '-'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 409: Forced or Compulsory Labour */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          409 : FORCED OR COMPULSORY LABOUR
        </p>
        <h1 className={styles.mainHeading}>
          409-1 : OPERATIONS AND SUPPLIERS AT SIGNIFICANT RISK FOR INCIDENTS OF FORCED OR COMPULSORY LABOUR
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Identify operations and suppliers */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. Identify operations and suppliers where there is a significant risk of incidents involving forced or compulsory labour? Please specify:
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {riskOperations || '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. The type of operation (e.g., manufacturing plant) and supplier.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {typeOfOperation || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Countries or geographic areas where operations and suppliers are considered at risk.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {countriesOrGeographic || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Part b: Actions taken */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. What actions did the organization take during the reporting period to contribute to the elimination of all forms of forced or compulsory labour?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {forcedLaborActions || '-'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 410: Security Practices 2016 */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          410 : SECURITY PRACTICES 2016
        </p>
        <h1 className={styles.mainHeading}>
          410-1 : SECURITY PERSONNEL TRAINED IN HUMAN RIGHTS POLICIES OR PROCEDURES
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Percentage of security personnel */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. What percentage of security personnel have undergone formal training on the organization's human rights policies or specific procedures, including their application to security?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {securityPercentage !== undefined && securityPercentage !== null && securityPercentage !== '' ? securityPercentage : '-'}
            </p>
          </div>
        </div>

        {/* Part b: Third-party organizations */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. Do the training requirements extend to third-party organizations that provide security personnel?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {thirdPartyOrgStatus 
                ? (thirdPartyOrgRemarks ? `Yes, ${thirdPartyOrgRemarks}` : 'Yes, all people trained')
                : 'No'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 411: Rights of Indigenous People */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          411 : RIGHTS OF INDIGENOUS PEOPLE
        </p>
        <h1 className={styles.mainHeading}>
          411-1 : INCIDENTS OF VIOLATIONS INVOLVING RIGHTS OF INDIGENOUS PEOPLES
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Number of incidents */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. Provide the number of incidents involving violations of the rights of indigenous peoples were identified during the reporting period?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {indigenousRightsViolationIncidents !== undefined && indigenousRightsViolationIncidents !== null && indigenousRightsViolationIncidents !== '' 
                ? indigenousRightsViolationIncidents 
                : '-'}
            </p>
          </div>
        </div>

        {/* Part b: Current status and actions */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. What is the current status of the incidents related to violations of indigenous peoples' rights, and what actions have been taken? Please indicate:
          </p>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Whether the incident has been reviewed by the organization.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {reviewOrganization ? 'Yes' : 'No'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. If remediation plans are being implemented.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {remediationPlans ? 'Yes' : 'No'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                3. Whether remediation plans have been implemented, and if so, provide details of the results reviewed through routine internal management review processes.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {indigenousRemediationPlansImplementedStatus 
                  ? (indigenousRemediationPlansImplementedRemarks ? `Yes, ${indigenousRemediationPlansImplementedRemarks}` : 'Yes')
                  : 'No'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                4. If the incident is no longer subject to action.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {indigenousNoLongerSubjectStatus 
                  ? (indigenousNoLongerSubjectRemarks ? `Yes, ${indigenousNoLongerSubjectRemarks}` : 'Yes')
                  : 'No'}
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Section 413: Local Communities */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          413 : LOCAL COMMUNITIES
        </p>
        <h1 className={styles.mainHeading}>
          413-1 : OPERATIONS WITH LOCAL COMMUNITY ENGAGEMENT, IMPACT ASSESSMENTS, AND DEVELOPMENT PROGRAMS
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Percentage of operations */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            a. Indicate the percentage of operations that have implemented local community engagement, impact assessments, and/or development programs, incorporating the following elements
          </p>

          {/* Sub-questions with percentage and remark */}
          <div style={{ marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Conducting environmental impact assessments and continuous monitoring.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {socialImpactAssessments?.percentage !== undefined && socialImpactAssessments?.percentage !== null && socialImpactAssessments?.percentage !== '' 
                  ? `${socialImpactAssessments.percentage}${socialImpactAssessments.remark ? `, ${socialImpactAssessments.remark}` : ''}`
                  : '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Public disclosure of results stemming from environmental and social impact assessments.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {publicDisclosure?.percentage !== undefined && publicDisclosure?.percentage !== null && publicDisclosure?.percentage !== '' 
                  ? `${publicDisclosure.percentage}${publicDisclosure.remark ? `, ${publicDisclosure.remark}` : ''}`
                  : '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                3. Development of local community development programs aligned with the needs of local communities.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {devlopmentLocalCommunity?.percentage !== undefined && devlopmentLocalCommunity?.percentage !== null && devlopmentLocalCommunity?.percentage !== '' 
                  ? `${devlopmentLocalCommunity.percentage}${devlopmentLocalCommunity.remark ? `, ${devlopmentLocalCommunity.remark}` : ''}`
                  : '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                4. Implementation of stakeholder engagement plans formulated based on stakeholder mapping.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {implmentationStackholder?.percentage !== undefined && implmentationStackholder?.percentage !== null && implmentationStackholder?.percentage !== '' 
                  ? `${implmentationStackholder.percentage}${implmentationStackholder.remark ? `, ${implmentationStackholder.remark}` : ''}`
                  : '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                5. Establishment of broad-based local community consultation committees and processes, inclusive of vulnerable groups.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {broadBased?.percentage !== undefined && broadBased?.percentage !== null && broadBased?.percentage !== '' 
                  ? `${broadBased.percentage}${broadBased.remark ? `, ${broadBased.remark}` : ''}`
                  : '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                6. Formation of works councils, occupational health and safety committees, and other worker representation bodies to address impacts
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {formationOfWorks?.percentage !== undefined && formationOfWorks?.percentage !== null && formationOfWorks?.percentage !== '' 
                  ? `${formationOfWorks.percentage}${formationOfWorks.remark ? `, ${formationOfWorks.remark}` : ''}`
                  : '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                7. Implementation of formal local community grievance processes.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {grievanceProcesses?.percentage !== undefined && grievanceProcesses?.percentage !== null && grievanceProcesses?.percentage !== '' 
                  ? `${grievanceProcesses.percentage}${grievanceProcesses.remark ? `, ${grievanceProcesses.remark}` : ''}`
                  : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 413-2: Operations with significant actual and potential negative impacts on local communities */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          413-2 : OPERATIONS WITH SIGNIFICANT ACTUAL AND POTENTIAL NEGATIVE IMPACTS ON LOCAL COMMUNITIES
        </h1>

        {/* Part a: Identify operations */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. Identify the operations with significant actual and potential negative impacts on local communities.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {locationOperation || '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. The location of the operations
              </p>
              {actualPotential && Array.isArray(actualPotential) && actualPotential.length > 0 ? (
                <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                  <Table className={tableStyles.table}>
                    <TableHead className={tableStyles.headerRow}>
                      <TableRow>
                        <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                          S.No.
                        </TableCell>
                        <TableCell className={tableStyles.headerCell}>
                          Location
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {actualPotential.map((item, index) => (
                        <TableRow 
                          key={index}
                          className={tableStyles.bodyRow}
                        >
                          <TableCell className={tableStyles.cell}>
                            {index + 1}.
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.location || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  -
                </p>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Describe the significant actual and potential negative impacts of operations in these locations.
              </p>
              {negativeImpactsOnLocalCommunities && Array.isArray(negativeImpactsOnLocalCommunities) && negativeImpactsOnLocalCommunities.length > 0 ? (
                <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
                  <Table className={tableStyles.table}>
                    <TableHead className={tableStyles.headerRow}>
                      <TableRow>
                        <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                          S.No.
                        </TableCell>
                        <TableCell className={tableStyles.headerCell}>
                          Location
                        </TableCell>
                        <TableCell className={tableStyles.headerCell}>
                          Actual Negative Impact
                        </TableCell>
                        <TableCell className={tableStyles.headerCell}>
                          Potential Negative Impact
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {negativeImpactsOnLocalCommunities.map((item, index) => (
                        <TableRow 
                          key={index}
                          className={tableStyles.bodyRow}
                        >
                          <TableCell className={tableStyles.cell}>
                            {index + 1}.
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.location || '-'}
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.actualnegativeImpact || '0'}
                          </TableCell>
                          <TableCell className={tableStyles.cell}>
                            {item.potentialnegativeImpact || '0'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                  No data available
                </p>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Section 414: Supplier Social Assessment */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          414 : SUPPLIER SOCIAL ASSESSMENT
        </p>
        <h1 className={styles.mainHeading}>
          414-1 : NEW SUPPLIERS THAT WERE SCREENED USING SOCIAL CRITERIA
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Percentage of new suppliers */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. What percentage of new suppliers underwent screening based on social criteria?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {supplierScreenedPercentage !== undefined && supplierScreenedPercentage !== null && supplierScreenedPercentage !== '' 
                ? `${supplierScreenedPercentage}${supplierScreenedRemark ? `, ${supplierScreenedRemark}` : ''}`
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 414-2: Negative social impacts in the supply chain and actions taken */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          414-2 : NEGATIVE SOCIAL IMPACTS IN THE SUPPLY CHAIN AND ACTIONS TAKEN
        </h1>

        {/* Part a: Suppliers evaluated */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. How many suppliers were evaluated for social impacts?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {evaluated?.percentage !== undefined && evaluated?.percentage !== null && evaluated?.percentage !== '' 
                ? `${evaluated.percentage}${evaluated.remark ? `, ${evaluated.remark}` : ''}`
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Part b: Suppliers identified */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. How many suppliers were identified as having significant actual and potential negative social impacts?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {identifiedSuppliers?.percentage !== undefined && identifiedSuppliers?.percentage !== null && identifiedSuppliers?.percentage !== '' 
                ? `${identifiedSuppliers.percentage}${identifiedSuppliers.remark ? `, ${identifiedSuppliers.remark}` : ''}`
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Part c: Negative social impacts */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            c. What significant actual and potential negative social impacts were identified within the supply chain?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {negativeSocialImpactsSupplyChain?.percentage !== undefined && negativeSocialImpactsSupplyChain?.percentage !== null && negativeSocialImpactsSupplyChain?.percentage !== '' 
                ? `${negativeSocialImpactsSupplyChain.percentage}${negativeSocialImpactsSupplyChain.remark ? `, ${negativeSocialImpactsSupplyChain.remark}` : ''}`
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Part d: Supplier agreement rate */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            d. What percentage of suppliers identified as having significant actual and potential negative social impacts agreed upon improvements as a result of assessment?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {supplierAgreementRate?.percentage !== undefined && supplierAgreementRate?.percentage !== null && supplierAgreementRate?.percentage !== '' 
                ? `${supplierAgreementRate.percentage}${supplierAgreementRate.remark ? `, ${supplierAgreementRate.remark}` : ''}`
                : 'No information provided.'}
            </p>
          </div>
        </div>

        {/* Part e: Supplier termination rate */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            e. What percentage of suppliers identified as having significant actual and potential negative social impacts had their relationships terminated as a result of assessment, and what were the reasons for termination?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {supplierTerminationRate?.percentage !== undefined && supplierTerminationRate?.percentage !== null && supplierTerminationRate?.percentage !== '' 
                ? `${supplierTerminationRate.percentage}${supplierTerminationRate.remark ? `, ${supplierTerminationRate.remark}` : ''}`
                : 'No information provided.'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 415: Public Policy */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          415 : PUBLIC POLICY
        </p>
        <h1 className={styles.mainHeading}>
          415-1 : POLITICAL CONTRIBUTIONS
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Political contributions table */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            a. Could you provide the total monetary value of financial and in-kind political contributions made directly and indirectly by the organization, categorized by country and recipient/beneficiary?
          </p>

          {/* Table: Political contributions */}
          {politicalContributionsByCountry && Array.isArray(politicalContributionsByCountry) && politicalContributionsByCountry.length > 0 ? (
            <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
              <Table className={tableStyles.table}>
                <TableHead className={tableStyles.headerRow}>
                  <TableRow>
                    <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                      S.No.
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Country
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Recipient/Beneficiary
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Contribution Type (Financial / in-kind)
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Monetary Value (in USD)
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Contribution Method (Direct / Indirect)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {politicalContributionsByCountry.map((item, index) => (
                    <TableRow 
                      key={index}
                      className={tableStyles.bodyRow}
                    >
                      <TableCell className={tableStyles.cell}>
                        {index + 1}.
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.country || '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.recipient || '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.contribution || '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.monetaryValue || '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.contributionMethod || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
              No data available
            </p>
          )}
        </div>

        {/* Part b: In-kind contributions explanation */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. Additionally, if applicable, please explain how the monetary value of in-kind contributions was estimated.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {financialPoliticalContributions || '-'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 416: Customer Health & Safety */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          416 : CUSTOMER HEALTH & SAFETY
        </p>
        <h1 className={styles.mainHeading}>
          416-1 : ASSESSMENT OF THE HEALTH AND SAFETY IMPACTS OF PRODUCTS AND SERVICE CATEGORIES
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Percentage of categories */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            a. What percentage of significant product and service categories undergo health and safety impact assessments for improvement?
          </p>

          {/* Table: Health and safety impact assessment */}
          {healthAndSafetyImpactAssessment && Array.isArray(healthAndSafetyImpactAssessment) && healthAndSafetyImpactAssessment.length > 0 ? (
            <div className={tableStyles.tableContainer} style={{ marginTop: '8px' }}>
              <Table className={tableStyles.table}>
                <TableHead className={tableStyles.headerRow}>
                  <TableRow>
                    <TableCell className={tableStyles.headerCell} style={{ width: '80px' }}>
                      S.No.
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Name of Category
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      %
                    </TableCell>
                    <TableCell className={tableStyles.headerCell}>
                      Remark
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {healthAndSafetyImpactAssessment.map((item, index) => (
                    <TableRow 
                      key={index}
                      className={tableStyles.bodyRow}
                    >
                      <TableCell className={tableStyles.cell}>
                        {index + 1}.
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.category || '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.percentage !== undefined && item.percentage !== null && item.percentage !== '' ? `${item.percentage}%` : '-'}
                      </TableCell>
                      <TableCell className={tableStyles.cell}>
                        {item.remarks || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p style={{ fontSize: '16px', color: '#666666', marginTop: '8px' }}>
              No data available
            </p>
          )}
        </div>
      </div>

      {/* Section 416-2: Incidents of non-compliance concerning the health and safety impacts of products and services */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          416-2 : INCIDENTS OF NON-COMPLIANCE CONCERNING THE HEALTH AND SAFETY IMPACTS OF PRODUCTS AND SERVICES
        </h1>

        {/* Part a: Incidents breakdown */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. How many incidents of non-compliance with regulations and/or voluntary codes regarding the health and safety impacts of products and services occurred within the reporting period? Please provide the following breakdown:
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {nonComplianceFinesPenalties?.percentage !== undefined && nonComplianceFinesPenalties?.percentage !== null && nonComplianceFinesPenalties?.percentage !== '' 
                ? `${nonComplianceFinesPenalties.percentage}${nonComplianceFinesPenalties.remark ? `, ${nonComplianceFinesPenalties.remark}` : ''}`
                : '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Incidents of non-compliance with regulations resulting in a fine or penalty.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {fineOrPenalty?.percentage !== undefined && fineOrPenalty?.percentage !== null && fineOrPenalty?.percentage !== '' 
                  ? (fineOrPenalty.percentage === '0' || fineOrPenalty.percentage === 0 
                      ? '-' 
                      : `${fineOrPenalty.percentage}${fineOrPenalty.remark ? `, ${fineOrPenalty.remark}` : ''}`)
                  : (fineOrPenalty?.remark || '-')}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Incidents of non-compliance with regulations resulting in a warning.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {resultingWaring?.percentage !== undefined && resultingWaring?.percentage !== null && resultingWaring?.percentage !== '' 
                  ? (resultingWaring.percentage === '0' || resultingWaring.percentage === 0 
                      ? '0' 
                      : `${resultingWaring.percentage}${resultingWaring.remark ? `, ${resultingWaring.remark}` : ''}`)
                  : (resultingWaring?.remark || '-')}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                3. Incidents of non-compliance with voluntary codes.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {voluntary?.percentage !== undefined && voluntary?.percentage !== null && voluntary?.percentage !== '' 
                  ? (voluntary.percentage === '0' || voluntary.percentage === 0 
                      ? '0' 
                      : `${voluntary.percentage}${voluntary.remark ? `, ${voluntary.remark}` : ''}`)
                  : (voluntary?.remark || '-')}
              </p>
            </div>
          </div>
        </div>

        {/* Part b: No non-compliance statement */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. If the organization did not identify any instances of non-compliance with regulations and/or voluntary codes, a brief statement confirming this fact is sufficient.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {noHealthSafetyNonCompliance?.remark || noHealthSafetyNonCompliance?.percentage || '-'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 417: Marketing & Labelling */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          417 : MARKETING & LABELLING
        </p>
        <h1 className={styles.mainHeading}>
          417-1 : REQUIREMENTS OF PRODUCT AND SERVICE INFORMATION AND LABELLING
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Types of information required */}
        <div style={{ marginBottom: '32px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal', marginBottom: '16px' }}>
            a. Does the organization's procedures for product and service information and labelling require the following types of information:
          </p>

          {/* Sub-questions */}
          <div style={{ marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Details regarding the sourcing of components used in the product or service?
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {componentSourcingDetails || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Information about the content, specifically substances that may have environmental or social impacts?
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {contentImpactDetails || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                3. Guidelines for the safe usage of the product or service?
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {safeUsageGuidelines || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                4. Instructions for the disposal of the product and its environmental or social impacts?
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {disposalInstructions || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                5. Other (please specify)?
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {otherProductInfo || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Part b: Percentage covered */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. What percentage of significant product or service categories are covered by these procedures and assessed for compliance?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {complianceRateProductCategories !== undefined && complianceRateProductCategories !== null && complianceRateProductCategories !== '' 
                ? complianceRateProductCategories 
                : 'No information provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 417-2: Incidents of non-compliance concerning product information and labelling */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          417-2 : INCIDENTS OF NON-COMPLIANCE CONCERNING PRODUCT INFORMATION AND LABELLING
        </h1>

        {/* Part a: Incidents breakdown */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. How many incidents of non-compliance with regulations and/or voluntary codes regarding product and service information and labeling occurred within the reporting period? Please provide the following breakdown:
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {reportingPeriodProduct || '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Incidents of non-compliance with regulations resulting in a fine or penalty.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {fineOrpenaltyProduct || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Incidents of non-compliance with regulations resulting in a warning.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {resultingWarningProduct || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                3. Incidents of non-compliance with voluntary codes.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {voluntarCodesProduct || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Part b: No non-compliance statement */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. If the organization did not identify any instances of non-compliance with regulations and/or voluntary codes regarding product and service information and labeling, a brief statement confirming this fact is sufficient.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {noNonComplianceProductInfo || '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Section 417-3: Incidents of non-compliance concerning marketing communications */}
      <div className={styles.consolidatingSection}>
        <h1 className={styles.mainHeading}>
          417-3 : INCIDENTS OF NON-COMPLIANCE CONCERNING MARKETING COMMUNICATIONS
        </h1>

        {/* Part a: Incidents breakdown */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. How many incidents of non-compliance with regulations and/or voluntary codes regarding marketing communications including advertising, promotion and sponsorship
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {reportingPeriodMarketing || '-'}
            </p>
          </div>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Incidents of non-compliance with regulations resulting in a fine or penalty.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {fineOrpenaltyMarketing || '-'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Incidents of non-compliance with regulations resulting in a warning.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {resultingWarningMarketing !== undefined && resultingWarningMarketing !== null && resultingWarningMarketing !== '' 
                  ? (resultingWarningMarketing === '0' || resultingWarningMarketing === 0 ? '0' : resultingWarningMarketing)
                  : '0'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                3. Incidents of non-compliance with voluntary codes.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {voluntarCodesMarketing !== undefined && voluntarCodesMarketing !== null && voluntarCodesMarketing !== '' 
                  ? (voluntarCodesMarketing === '0' || voluntarCodesMarketing === 0 ? '0' : voluntarCodesMarketing)
                  : '0'}
              </p>
            </div>
          </div>
        </div>

        {/* Part b: No non-compliance statement */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. If the organization has not identified any non-compliance with regulations and/or voluntary codes, a brief statement of this fact is sufficient.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {noNonComplianceMarketing || '-'}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Section 418: Customer Privacy */}
      <div className={styles.container}>
        <p className={styles.sectionHeading}>
          418 : CUSTOMER PRIVACY
        </p>
        <h1 className={styles.mainHeading}>
          418-1 : SUBSTANTIATED COMPLAINTS CONCERNING BREACHES OF CUSTOMER PRIVACY AND LOSSES OF CUSTOMER DATA
        </h1>
        
        <div className={styles.consolidatingSection}>

        {/* Part a: Complaints received */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            a. How many substantiated complaints concerning breaches of customer privacy were received? Please categorize them as follows:
          </p>

          {/* Sub-questions */}
          <div style={{ marginTop: '16px', marginLeft: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                1. Complaints received from external parties and substantiated by the organization.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {complaintsReceived?.percentage !== undefined && complaintsReceived?.percentage !== null && complaintsReceived?.percentage !== '' 
                  ? `${complaintsReceived.percentage}${complaintsReceived.remark ? `, ${complaintsReceived.remark}` : ''}`
                  : (complaintsReceived?.remark || '-')}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className={`${styles.questionTitle} common_text_style`} style={{ marginBottom: '8px' }}>
                2. Complaints from regulatory bodies.
              </p>
              <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
                {regulatoryBodies?.percentage !== undefined && regulatoryBodies?.percentage !== null && regulatoryBodies?.percentage !== '' 
                  ? `${regulatoryBodies.percentage}${regulatoryBodies.remark ? `, ${regulatoryBodies.remark}` : ''}`
                  : (regulatoryBodies?.remark || '-')}
              </p>
            </div>
          </div>
        </div>

        {/* Part b: Data leaks, thefts, or losses */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            b. How many leaks, thefts, or losses of customer data were identified?
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {customerDataIdentified?.percentage !== undefined && customerDataIdentified?.percentage !== null && customerDataIdentified?.percentage !== '' 
                ? `${customerDataIdentified.percentage}${customerDataIdentified.remark ? `, ${customerDataIdentified.remark}` : ''}`
                : (customerDataIdentified?.remark || '-')}
            </p>
          </div>
        </div>

        {/* Part c: No complaints statement */}
        <div className={styles.questionBox} style={{ marginBottom: '24px' }}>
          <p className={styles.questionTitle} style={{ fontWeight: 'normal' }}>
            c. If the organization did not identify any substantiated complaints regarding breaches of customer privacy, a brief statement confirming this fact is sufficient.
          </p>
          <div style={{ marginTop: '8px', marginLeft: '0' }}>
            <p className={styles.questionAnswer} style={{ fontWeight: 'normal' }}>
              {noPrivacyComplaints?.remark || noPrivacyComplaints?.percentage || '-'}
            </p>
          </div>
        </div>
        </div>
      </div>
    </>
  )
}

export default GRI400
